/**
 * Tournament setup.
 *
 * The flow follows how the week is actually built: who's playing and off what
 * handicap, then a block of tee times on a course with a format attached, then
 * let the app draw the pairings.
 */

import { useEffect, useRef, useState } from 'react'
import { clockTime } from '../engine/clock'
import { optimize, fairnessReport, sidesFor, contestsFor, type RoundSpec } from '../engine/pairings'
import {
  groupOf,
  needsContests,
  redrawContests,
  seatIn,
  seatOut,
  seatingProblems,
  swapSeats,
  unseated,
} from '../engine/foursomes'
import { courseHandicapFor, findTee, holesFor, playingIndex, recommendTee, teeProblems, teesWithBadPar, unassignedTees } from '../engine/handicap'
import type { Group, Match, Pairing, Round, Tournament } from '../engine/types'
import {
  BANDON_COURSES,
  IDAHO_COURSES,
  FORMAT_PRESETS,
  configFor,
  type FormatPresetId,
} from '../data/seed'
import { freezeHandicaps, frozenRoundIds, playerName, roundDay, staleFrozenRounds } from '../select'
import { matchNameIn, matchNumber, nextRoundLabel, roundName } from '../engine/naming'
import { inPlayOrder, weekdayOf } from '../select'
import { flightProblems } from '../engine/leaderboard'
import { computeAll, fillFlightGroups } from '../select'
import { createTournament, demote, mergedConfirmations, mergedScores, mergedSideMatches, resetScores, saveDoc, touchAdmin, useStore } from '../store'
import { Bar, SectionRule, UnverifiedBanner } from '../ui'
import { CardPhotoButton } from './CardPhoto'
import { CourseEditor } from './CourseEditor'
import { PayoutEditor, PayoutTotals } from './Payouts'

const uid = () => crypto.randomUUID().slice(0, 8)

/**
 * What would go wrong if the week started right now.
 *
 * These used to be scattered: the flight ranges refused the save, a missing
 * draw showed up as an empty Score tab, and a round with no course simply
 * scored nothing. None of them announced themselves at the moment they could
 * still be fixed cheaply, which is before anyone has entered a score.
 *
 * Deliberately not a lock — starting records that the week has begun and
 * nothing more. The list is the value; refusing the button is only there to
 * make somebody read it.
 */
function startProblems(t: Tournament): string[] {
  const out: string[] = []
  const active = t.players.filter((p) => p.active)

  if (active.length === 0) out.push('Nobody is playing yet — add the roster.')
  else if (active.length % 4 !== 0) {
    out.push(`${active.length} players will not divide into foursomes — the draw needs a multiple of four.`)
  }
  if (t.rounds.length === 0) out.push('No rounds scheduled.')

  /*
   * A guessed tee must not start a week. With no assignment anywhere, findTee
   * quietly prices a man off the tips — arithmetically perfect, for a tee
   * nobody picked, worth a phantom pop against whoever draws him. Caught in
   * the dry run on a real screenshot; blocked here for the real thing.
   */
  for (const name of unassignedTees(t)) {
    out.push(`${name} has no tee picked on any course — he would be priced off the tips.`)
  }

  /*
   * A handicap frozen for nine holes, on a round that now plays eighteen.
   *
   * The frozen figure is for the holes THAT ROUND PLAYED at the time — already
   * halved for a nine — and the course and the nine stay editable afterwards.
   * Change front to All 18 and the quota target is computed over eighteen holes
   * against half an allowance: the target comes out about half a handicap too
   * high, and every pop is halved. It is detected today, in a panel three
   * screens away, under copy that says the difference is "usually right". This
   * is the one place that has the admin's attention before anybody tees off.
   */
  for (const roundId of staleFrozenRounds(t)) {
    const r = t.rounds.find((x) => x.id === roundId)
    out.push(
      `${r?.label ?? 'A round'} has handicaps written down that no longer match its card — rewrite them under Handicaps, or its targets and pops will be wrong.`,
    )
  }

  for (const r of t.rounds) {
    if (!t.courses.some((c) => c.id === r.courseId)) {
      out.push(`${r.label} has no course.`)
      continue
    }
    if (!t.groups.some((g) => g.roundId === r.id)) {
      out.push(`${r.label} has no draw — generate the pairings.`)
    }
    /*
     * A foursome edited by hand can leave a man in no group, or a group with
     * nobody paired off — and a format that scores off pairings scores nothing
     * at all for that foursome. Both are silent everywhere else on the screen,
     * which is exactly the shape of thing this list exists to catch before
     * anybody tees off. Said per round, because the fix is per round.
     */
    out.push(...seatingProblems(t, r.id).map((x) => `${r.label}: ${x}`))
  }

  for (const m of t.matches) {
    if (m.config.kind === 'strokeNet' && m.config.flighted) {
      out.push(...flightProblems(m.config.flights, active.length).map((x) => `${matchNameIn(t, m)}: ${x}`))
    }
  }

  const unverified = t.courses.filter((c) => !c.verified && t.rounds.some((r) => r.courseId === c.id))
  for (const c of unverified) {
    out.push(`${c.name} has not been checked against the physical card.`)
  }

  return out
}

/**
 * The four directions as pitched, with the swatches from the original board.
 *
 * The blurbs are the honest trade each one makes rather than a sales line —
 * this is read once, by an admin, deciding what twelve people stare at for five
 * days in coastal glare.
 */
const THEMES = [
  {
    id: 'broadcast' as const,
    name: 'Broadcast',
    blurb: 'The Sunday leaderboard graphic. Loudest and the easiest to read at arm’s length.',
    swatches: ['#FFFFFF', '#0A0A0A', '#D8232A'],
  },
  {
    id: 'broadcast-soft' as const,
    name: 'Broadcast, softened',
    blurb: 'The same identity with off-white paper and the red pulled back. Easier for five days.',
    swatches: ['#FAF9F6', '#17181A', '#C1362F'],
  },
  {
    id: 'clubhouse' as const,
    name: 'Clubhouse',
    blurb: 'Hunter green and parchment, engraved serif. The crest given a room. Dimmest in sun.',
    swatches: ['#14261C', '#F2EBDC', '#B08D33'],
  },
  {
    id: 'fescue' as const,
    name: 'Fescue',
    blurb: 'Dark by default, one gorse yellow, figures in a column. Best on the course, least ceremony.',
    swatches: ['#0C120F', '#D8F24B', '#7D9B84'],
  },
  {
    id: 'southcoast' as const,
    name: 'South Coast',
    blurb: 'Bandon itself — fog gray, Pacific navy, gorse. Calmest, and the best in the recap.',
    swatches: ['#DCE1E0', '#16323F', '#E0A80D'],
  },
]

const roundTypeFor = (kind: Match['config']['kind']): RoundSpec['type'] =>
  kind === 'individualMatchPlay'
    ? 'oneVsOne'
    : kind === 'teamHighLow' || kind === 'teamCombinedNet' || kind === 'teamFourBall'
      ? 'twoVsTwo'
      : 'freeForAll'

export function Admin({ doc }: { doc: Tournament }) {
  const serverVersion = useStore((s) => s.docVersion)

  const [draft, seedDraft] = useState<Tournament>(doc)
  /**
   * The version the thing on screen was forked from.
   *
   * This is what a save has to quote back, and it is emphatically not the sync
   * cursor: the cursor is dragged forward by every score every phone enters, so
   * quoting it made the optimistic check answer a question about scores. It
   * moves only when the draft is re-seeded or a save succeeds — the two moments
   * the draft genuinely becomes a fork of something new.
   */
  const [fork, setFork] = useState(serverVersion)
  /** Has this draft diverged from what the server holds? */
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  /*
   * Which of the four shelves is on the bench.
   *
   * Sub-tabs rather than sub-screens, and state INSIDE this component rather
   * than a route, on purpose: the CAS draft, its fork version and the 1.2s
   * debounce all live here, and unmounting Admin mid-edit would take a dirty
   * draft with it. Switching shelves therefore never unmounts anything that
   * matters — it only chooses what renders.
   */
  const [section, setSection] = useState<'golf' | 'field' | 'trip' | 'run'>('golf')
  const [armedRound, setArmedRound] = useState<string | null>(null)
  const [armedUnpaid, setArmedUnpaid] = useState<string | null>(null)
  // Two-tap arm for calling a round on darkness — same shape as Remove.
  const [armedCall, setArmedCall] = useState<string | null>(null)
  // A refused edit says why, where the edit was attempted.
  const [banner, setBanner] = useState<string | null>(null)
  // Open only when a card still needs checking — the one state worth reading.
  const [coursesOpen, setCoursesOpen] = useState(() =>
    doc.courses.some((c) => !c.verified),
  )
  const [newPlayer, setNewPlayer] = useState('')
  /*
   * The roster reads rather than edits until asked.
   *
   * Not a lock — the PIN already did that. This is about what the screen
   * looks like it wants: twelve rows of live inputs invite a stray tap on a
   * number that decides everybody's strokes for the week.
   */
  const [rosterEdit, setRosterEdit] = useState(false)
  const [openCourse, setOpenCourse] = useState<string | null>(null)
  /*
   * Scores and signatures, because two of this screen's decisions depend on
   * them: filling the championship flights (which needs the settled standings)
   * and the guards on redrawing or removing a player (which must know whether
   * anything has been signed — a signed card is money, and money does not
   * survive its group id changing).
   */
  const liveScores = useStore(mergedScores)
  const liveConfirmations = useStore(mergedConfirmations)
  const liveSideMatches = useStore(mergedSideMatches)
  /*
   * Matches every group has signed for — the ones whose money is already
   * counted. Computed off the SAVED document rather than the draft, because it
   * is a fact about what has happened, not about what is being edited.
   */
  const settledMatchIds = computeAll(doc, liveScores, liveConfirmations).finalMatchIds
  // Armed-destructive-action state: the id (or sentinel) armed for a second tap.
  const [armedRemove, setArmedRemove] = useState<string | null>(null)
  const [armedRedraw, setArmedRedraw] = useState(false)
  const [armedShuffle, setArmedShuffle] = useState(false)
  const [armedFill, setArmedFill] = useState(false)
  /*
   * Which round's foursomes are open for editing, which round has been warned
   * about, and who is currently picked up.
   *
   * One round at a time and one man at a time on purpose: this is used
   * one-handed on a tee, and a screen offering twelve live controls across nine
   * rounds is a screen where a stray thumb moves somebody.
   */
  const [seatingRound, setSeatingRound] = useState<string | null>(null)
  const [armedSeating, setArmedSeating] = useState<string | null>(null)
  const [picked, setPicked] = useState<string | null>(null)
  // Which course's card is editable, kept apart from `openCourse` the way the
  // roster keeps `rosterEdit`: reading a card is the common case and must not
  // look like a form that wants filling in.
  const [editCourse, setEditCourse] = useState<string | null>(null)

  /*
   * A hidden shelf must not keep a loaded gun.
   *
   * Half the armed two-press buttons disarm on blur, half do not — so an
   * armed "Tap again to redraw" could sit live and invisible behind another
   * tab, waiting for a tap that thinks it is the FIRST press. Changing
   * shelves stands every one of them down, and closes the seating editor
   * for the same reason.
   */
  useEffect(() => {
    setArmedRound(null)
    setArmedUnpaid(null)
    setArmedCall(null)
    setArmedRemove(null)
    setArmedRedraw(false)
    setArmedShuffle(false)
    setArmedFill(false)
    setArmedSeating(null)
    setPicked(null)
    setSeatingRound(null)
  }, [section])

  // Every edit below goes through here, so `dirty` cannot drift out of step
  // with the draft by someone adding a section and forgetting to set it.
  const setDraft = (next: Tournament) => {
    seedDraft(next)
    setDirty(true)
    /*
     * Every edit pushes the idle lock back.
     *
     * This is the single funnel all of Setup writes through, which is what
     * makes a ten-minute lock safe: somebody retyping twelve handicaps or
     * building a draw is continuously refreshing it, so it can only ever fire
     * on a screen nobody is using. Without this the lock would land in the
     * middle of the longest job on the screen and take the draft with it.
     */
    touchAdmin()
  }

  /*
   * Follow the server while there is nothing to lose, and stop dead once there
   * is.
   *
   * The draft used to be seeded from the prop exactly once and never again,
   * while polling replaced that prop every few seconds — so a Setup tab left
   * open drifted arbitrarily far behind, and a save from it wrote a document
   * forked from whatever the week looked like when the tab was opened. Taking
   * the new one whenever nothing has been typed keeps that from ever building
   * up; refusing to once something has is the whole point of a draft.
   */
  useEffect(() => {
    if (dirty || serverVersion === fork) return
    seedDraft(doc)
    setFork(serverVersion)
  }, [doc, serverVersion, dirty, fork])

  /*
   * Setup saves itself.
   *
   * There is no reason a screen full of half-finished configuration should have
   * a save button at all — the admin is standing in a car park adding a player
   * and adjusting a handicap, and every one of those edits is worth keeping the
   * moment it is made. Waiting for a deliberate tap meant an afternoon of work
   * could sit in a tab that a phone was free to discard.
   *
   * Debounced rather than immediate because a handicap index is typed a digit
   * at a time, and 18.4 passes through 1, 18 and 18. on its way — three saves
   * and two of them nonsense.
   */
  const lastTried = useRef<Tournament | null>(null)
  useEffect(() => {
    if (!dirty || saving) return
    /*
     * One attempt per version of the draft.
     *
     * A failed save leaves `dirty` true, so without this the effect would fire
     * again in another 1.2 seconds, and again, hammering the server forever
     * over a conflict that only a human can resolve. Editing anything makes a
     * new draft object and arms it again.
     */
    if (lastTried.current === draft) return
    const t = setTimeout(() => {
      lastTried.current = draft
      void save(draft)
    }, 1200)
    return () => clearTimeout(t)
  }, [draft, dirty, saving])

  const save = async (next: Tournament) => {
    /*
     * Everything saves. Only starting is gated.
     *
     * A broken flight range used to refuse the save outright, which meant that
     * adding a thirteenth player — leaving seed 13 in no flight — silently
     * blocked every unrelated edit after it: handicaps, pairings, the roster
     * itself. The admin typed, tapped save, and watched the old names come
     * back, with the reason two screens above them.
     *
     * Setup is a workbench: half-finished is its normal state, and losing work
     * is worse than storing something not yet playable. The money checks moved
     * to "Start the tournament", which is the moment they actually mean
     * something.
     */
    seedDraft(next)
    setSaving(true)
    try {
      // Saved against the fork point, so a score landing since the last poll
      // cannot be mistaken for someone else editing the setup.
      setFork(await saveDoc(next, fork))
      setDirty(false)
      setNote('Saved')
    } catch (e) {
      /*
       * There is no doc retry queue, so the old catch-all was a lie.
       *
       * "It will retry when you are back on signal" is true of scores and of
       * nothing else on this screen — a failed setup save is simply gone. The
       * expiring case is the one that will actually happen: an admin token
       * lasts a day and the trip is five, so on Wednesday every save fails with
       * a 403 while Setup keeps rendering, because `isAdmin` is client state
       * and never expires. Saying "back on signal" sends somebody to look for
       * bars when what they need is to type four digits.
       */
      const status = (e as { status?: number }).status
      setNote(
        e instanceof Error && e.message === 'conflict'
          ? 'The setup changed underneath you — your edits are still here, but reload before saving.'
          : status === 401 || status === 403
            ? 'Your setup access has expired. Your edits are still here — unlock with the PIN again, then save.'
            : status === undefined
              ? 'No signal — this did not save. Your edits are still here; try again when you have service.'
              : `Could not save (${status}). Your edits are still here.`,
      )
      // Drop the stale admin flag so the PIN screen comes back on the next
      // visit to Setup rather than leaving a dead screen that looks alive.
      if (status === 401 || status === 403) demote()
    } finally {
      setSaving(false)
      setTimeout(() => setNote(null), 3000)
    }
  }

  // --- players -------------------------------------------------------------

  const setBuyIn = (id: string, paidAt: string | null) =>
    setDraft({
      ...draft,
      players: draft.players.map((p) => (p.id === id ? { ...p, buyInPaidAt: paidAt } : p)),
    })

  const setHandicap = (id: string, v: number) =>
    setDraft({
      ...draft,
      players: draft.players.map((p) => (p.id === id ? { ...p, handicapIndex: v } : p)),
    })

  /*
   * Blank is a real answer here, unlike the index.
   *
   * Everybody has a playing index; the Low HI has to be looked up per man in
   * GHIN, so most of the field will not have one for most of the week. Storing
   * a 0 for "not asked yet" would print a scratch low index beside a 26 index,
   * which is worse than printing nothing.
   */
  const setLowIndex = (id: string, raw: string) =>
    setDraft({
      ...draft,
      players: draft.players.map((p) =>
        p.id === id ? { ...p, lowIndex: raw.trim() === '' ? null : Number(raw) } : p,
      ),
    })

  /*
   * Which of his two numbers he plays off — a pointer, never a copy.
   *
   * Per man rather than for the week: somebody three shots above his low is
   * a different argument from somebody sitting on it, and one switch for
   * twelve people would force the same verdict on both.
   */
  const setPlaysOff = (id: string, v: 'current' | 'low') =>
    setDraft({
      ...draft,
      players: draft.players.map((p) => (p.id === id ? { ...p, playsOff: v } : p)),
    })

  const setTee = (playerId: string, courseId: string, teeId: string) =>
    setDraft({
      ...draft,
      players: draft.players.map((p) =>
        p.id === playerId
          ? { ...p, teeByCourse: { ...p.teeByCourse, [courseId]: teeId } }
          : p,
      ),
    })

  const addPlayer = () => {
    const name = newPlayer.trim()
    if (!name) return
    // The id is what a player signs in as and what every score row points at,
    // so it is derived once from the name and then never changes.
    const id = name.toLowerCase().replace(/[^a-z0-9]+/g, '')
    if (!id) return setNote('That name has no letters or numbers in it.')
    if (draft.players.some((p) => p.id === id)) return setNote(`${name} is already on the list.`)
    setDraft({
      ...draft,
      players: [
        ...draft.players,
        {
          id,
          displayName: name,
          fullName: name,
          aliases: [],
          handicapIndex: 0,
          teeByCourse: {},
          isAdmin: false,
          active: true,
        },
      ],
    })
    setNewPlayer('')
  }

  /**
   * Drop a player, and everything keyed to them.
   *
   * Leaving them in a group or a pairing would hand the optimizer a foursome
   * with a hole in it, so the draw for any round they appear in is cleared and
   * has to be redrawn. Their scores are not touched: those live server-side
   * keyed on player id, and a re-added player should find them again.
   */
  const removePlayer = (id: string) => {
    const touched = new Set(
      draft.groups.filter((g) => g.playerIds.includes(id)).map((g) => g.roundId),
    )
    /*
     * A man standing in a SIGNED round cannot be removed, only marked inactive.
     *
     * Removing him clears the draw for every round he appears in, and clearing
     * a signed round's draw orphans its confirmations — the cards stay signed
     * in the database while nothing on any screen can find them, so settled
     * matches quietly unsettle. The audit rated one unconfirmed x-tap here as
     * able to zero the standings mid-week. Inactive keeps his rounds, his
     * signatures and his money exactly where they are.
     */
    const signedRounds = new Set(liveConfirmations.map((c) => c.roundId))
    const inSigned = [...touched].some((rid) => signedRounds.has(rid))
    if (inSigned) {
      setNote(
        `${playerName(draft, id)} is in a round that has been signed for. Removing him would orphan those signatures — mark him inactive instead.`,
      )
      setArmedRemove(null)
      return
    }
    // Two taps. The first says exactly what the second will do.
    if (armedRemove !== id) {
      setArmedRemove(id)
      setNote(
        `Removing ${playerName(draft, id)} clears the draw for ${touched.size || 'no'} round${touched.size === 1 ? '' : 's'}. Tap again to do it.`,
      )
      return
    }
    setArmedRemove(null)
    setDraft({
      ...draft,
      players: draft.players.filter((p) => p.id !== id),
      handicaps: draft.handicaps.filter((h) => h.playerId !== id),
      groups: draft.groups.filter((g) => !touched.has(g.roundId)),
      pairings: draft.pairings.filter((p) => {
        const round = draft.rounds.find((r) =>
          draft.matches.some((m) => m.id === p.matchId && m.roundId === r.id),
        )
        return !round || !touched.has(round.id)
      }),
    })
    if (touched.size > 0) {
      setNote(`Removed. ${touched.size} round${touched.size > 1 ? 's need' : ' needs'} a new draw.`)
    }
  }

  /*
   * Put the twelve into the championship's three foursomes, from the standings.
   *
   * This is the tap the whole week funnels into, and until the pre-trip audit it
   * did not exist: the flighted round's groups held the optimizer's placeholder
   * names forever, every phone's Score tab defaulted to the wrong foursome on
   * Wednesday afternoon, and the only lever that touched groups at all —
   * regenerating the draw — minted new group ids and orphaned every signed card.
   *
   * The group ids are PRESERVED, which is most of the point: signatures and
   * settlements key on roundId~groupId, so membership has to change in place.
   * Flight N's men go into the group STAMPED flight N — the stamp, not the slot,
   * because the admin may have reordered tee times to send Flight 1 out last.
   *
   * Refuses while the seeding is incomplete rather than filling from part of the
   * week: a fill off eleven signed cards would be authoritative-looking and
   * wrong, which is exactly the shape of thing this app exists to not do.
   */
  const fillFlights = (roundId: string) => {
    const view = computeAll(draft, liveScores, liveConfirmations)
    if (!view.seedingComplete) {
      setNote('Not yet — the flights are seeded off the standings, and cards are still unsigned. The Board names them.')
      return
    }
    if (view.flights.length === 0) {
      setNote('No flights are configured on the championship.')
      return
    }
    if (!armedFill) {
      setArmedFill(true)
      setNote(
        'Filling the flights writes the championship foursomes from the standings, replacing whoever is in them now. Tap again to fill.',
      )
      return
    }
    setArmedFill(false)
    const groups = fillFlightGroups(draft, roundId, view.flights)
    const filled = groups.filter(
      (g, i) => g.roundId === roundId && g !== draft.groups[i],
    ).length
    let next = { ...draft, groups }
    /*
     * Re-freeze THIS round's handicaps in the same tap. The freeze snapshots by
     * group membership, so the men just placed would otherwise derive live —
     * and a GHIN revision between now and the first tee would move champions'
     * allowances after the draw was announced. One tap, one consistent state.
     */
    if (draft.handicaps.length > 0) {
      next = { ...next, handicaps: freezeHandicaps(next, [roundId]) }
    }
    next = { ...next, settings: { ...next.settings, flightsFilledAt: new Date().toISOString() } }
    setDraft(next)
    setNote(
      `${filled} foursomes set from the standings${draft.handicaps.length > 0 ? ', and their handicaps written down' : ''}. The draw below shows the names.`,
    )
  }

  // --- rounds --------------------------------------------------------------

  const addRound = () => {
    const seq = draft.rounds.length + 1
    const course = draft.courses[0]
    if (!course) return

    /*
     * A new round starts on the day after the last one, not on day one.
     *
     * Same course AND same date is how the draw recognizes one outing and keeps
     * the foursomes together across the front and the back — which is correct
     * and wanted. But defaulting every new round to the start date made every
     * round look like one enormous outing, so six rounds drew the same
     * foursomes and forty-eight pairs never met each other all week. Nothing
     * said so.
     *
     * Stepping the day means the default is a distinct outing, and putting two
     * rounds on one day stays a deliberate edit — which is the right way round.
     */
    const last = [...draft.rounds].sort((a, b) => a.seq - b.seq).at(-1)
    const nextDate = (() => {
      if (!last?.date) return draft.startDate
      const d = new Date(`${last.date}T12:00:00Z`)
      d.setUTCDate(d.getUTCDate() + 1)
      return d.toISOString().slice(0, 10)
    })()

    const round: Round = {
      id: uid(),
      seq,
      label: `Round ${seq}`,
      courseId: course.id,
      nine: 'front',
      date: nextDate,
      firstTeeTime: '09:00',
      teeIntervalMinutes: 10,
    }
    const match: Match = {
      id: uid(),
      roundId: round.id,
      seq,
      label: `Match ${seq}`,
      config: configFor('individualMatchPlay'),
      status: 'scheduled',
    }
    setDraft({ ...draft, rounds: [...draft.rounds, round], matches: [...draft.matches, match] })
  }

  /** The match played on a round. One each, by construction. */
  const matchOf = (roundId: string) => draft.matches.find((m) => m.roundId === roundId)

  /**
   * The number an admin types, which is the only stored part of a match's name.
   *
   * Trimmed and capped, and emptiness is preserved rather than coerced: the
   * championship genuinely has no number, and a blank field must be able to
   * mean that rather than snapping back to whatever the old label said.
   */
  const patchMatchNumber = (roundId: string, value: string) => {
    const number = value.trim().slice(0, 4)
    setDraft({
      ...draft,
      matches: draft.matches.map((m) => (m.roundId === roundId ? { ...m, number } : m)),
    })
    touchAdmin()
  }

  const patchRound = (id: string, patch: Partial<Round>) => {
    /*
     * Re-sorted and renumbered on every edit, not just re-rendered.
     *
     * Moving a round to another day or another tee time changes where it sits
     * in the week, and `seq` is what every other screen reads for that — the
     * day sections, the round a scorer lands on, the tiebreak rules. Sorting
     * only the list on this screen would leave Setup right and everywhere else
     * wrong.
     */
    /*
     * A label that has not been renamed follows its round.
     *
     * Moving a round to another course left the label saying the old one, and
     * the label is on the card masthead, the birdie feed, the outstanding-cards
     * nag, the side-match picker and every shared link — where it appeared
     * beside the DERIVED match name, so one line read both "Bandon" and "Sheep
     * Ranch". A name somebody typed is left alone; see `nextRoundLabel`.
     */
    /*
     * Moving a round to another course or nine, with golf already on it, is
     * refused rather than warned about. Scores are keyed by hole number and a
     * signature covers the card that was signed — both would silently attach
     * to different holes on different ground. The parking-lot case this
     * exists for ("the starter found us a slot on Pacific") is always a round
     * nobody has played yet.
     */
    const moving = patch.courseId !== undefined || patch.nine !== undefined
    if (moving) {
      const target = draft.rounds.find((r) => r.id === id)
      const hasScores = liveScores.some((sc) => sc.roundId === id)
      const isSigned = liveConfirmations.some((c) => c.roundId === id)
      if (hasScores || isSigned) {
        setBanner(
          `${target?.label ?? 'That round'} already has ${
            isSigned ? 'a signed card' : 'scores'
          } on it — clear them before moving it to another course or nine.`,
        )
        return
      }
    }
    const rounds = inPlayOrder(
      draft.rounds.map((r) => {
        if (r.id !== id) return r
        const moved = { ...r, ...patch }
        const nameOf = (x: typeof r) =>
          roundName(x, draft.courses.find((c) => c.id === x.courseId))
        return { ...moved, label: nextRoundLabel(moved.label, nameOf(r), nameOf(moved)) }
      }),
    )
    /*
     * Frozen handicaps follow the move.
     *
     * The freeze pins each man's CH against the card he will actually play. A
     * moved round invalidated the snapshot and left the repair three screens
     * away, behind a warning — the one moment nobody has time for it, since
     * this edit happens with the starter on the phone. Derived rows are
     * re-derived against the new card in the same save; a row somebody typed
     * by hand is theirs and stays.
     */
    const refreeze = moving && draft.handicaps.some((h) => h.roundId === id)
    const handicaps = refreeze
      ? [
          ...draft.handicaps.filter(
            (h) => h.roundId !== id || h.source === 'manual',
          ),
          ...freezeHandicaps({ ...draft, rounds }, [id]).filter(
            (h) =>
              !draft.handicaps.some(
                (m) => m.roundId === id && m.playerId === h.playerId && m.source === 'manual',
              ),
          ),
        ]
      : draft.handicaps

    /*
     * Tee times are derived, so they have to be re-derived.
     *
     * A group's `teeTime` is computed at draw time from the round's first time
     * and interval and then stored. Editing either afterwards left every group
     * displaying the old spacing — the number on the sheet, on the cards and in
     * the share text, all quietly wrong and all disagreeing with the two fields
     * that were just changed.
     */
    const retime = patch.firstTeeTime !== undefined || patch.teeIntervalMinutes !== undefined
    if (!retime) {
      setDraft({ ...draft, rounds, handicaps })
      return
    }
    const round = rounds.find((r) => r.id === id)!
    const [h, m] = round.firstTeeTime.split(':').map(Number)
    setDraft({
      ...draft,
      rounds,
      handicaps,
      groups: draft.groups.map((g) => {
        if (g.roundId !== id) return g
        const minutes = (h ?? 9) * 60 + (m ?? 0) + g.index * round.teeIntervalMinutes
        return {
          ...g,
          teeTime: `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`,
        }
      }),
    })
  }

  /*
   * Changing the format throws away that match's pairings.
   *
   * The pairings ARE the format: individual match play draws two singles inside
   * a foursome, a team format draws one 2v2. Swapping the format and keeping
   * the old pairings left a High/Low round holding twelve one-man "teams", each
   * collecting both the low-ball and the high-ball point on every hole — a
   * silently and wildly wrong scoreboard, with nothing on screen to suggest
   * anything had happened.
   *
   * So they go, and it says so. Losing a draw is annoying; scoring a week
   * against a draw that does not match its format is worse.
   */
  const setFormat = (matchId: string, preset: FormatPresetId) => {
    const match = draft.matches.find((m) => m.id === matchId)
    const had = draft.pairings.some((p) => p.matchId === matchId)
    setDraft({
      ...draft,
      matches: draft.matches.map((m) =>
        m.id === matchId
          ? // The label is NOT rewritten. The name is derived from the number,
            // the course and the format — see `matchName` — and a stored copy is
            // exactly what used to go stale the moment a round moved.
            { ...m, config: configFor(preset) }
          : m,
      ),
      pairings: draft.pairings.filter((p) => p.matchId !== matchId),
    })
    if (had) {
      setNote(
        `${matchNameIn(draft, match!)} changed format. Its pairings were cleared — generate the draw again — and its points and money are back to the sheet's numbers for that format, so check what it pays.`,
      )
    }
  }

  /*
   * Asked twice, because it is less reversible than it looks.
   *
   * Scores already entered stay in the database keyed to a round id that no
   * longer exists, and re-adding the round mints a fresh one — so those scores
   * are gone from the app for good. The control also sits a thumb's width from
   * the label field somebody taps to type in.
   */
  const removeRound = (id: string) => {
    if (armedRound !== id) {
      setArmedRound(id)
      /*
       * Say the cost, the way the redraw and the roster already do.
       *
       * This was the one control in Setup that can un-settle money and had no
       * words on it at all: the first tap only changed the button to "Tap
       * again". Scores key on the round id and signatures on roundId~groupId,
       * so both are orphaned the moment it goes, and re-adding the round mints
       * a fresh id — they do not come back.
       */
      const signed = liveConfirmations.filter((c) => c.roundId === id).length
      const played = new Set(
        liveScores.filter((sc) => sc.roundId === id).map((sc) => `${sc.playerId}~${sc.holeNo}`),
      ).size
      const round = draft.rounds.find((r) => r.id === id)
      /*
       * Side matches name their rounds, and the holes are re-read on every
       * render — so removing one is removing holes from a bet two men shook on.
       */
      const bets = liveSideMatches.filter((sm) => !sm.voidedAt && sm.roundIds.includes(id)).length
      setNote(
        played === 0 && signed === 0
          ? `Remove ${round?.label ?? 'this round'}? Nothing has been scored on it. Tap again.`
          : `${round?.label ?? 'This round'} has ${played} score${played === 1 ? '' : 's'} on it${
              signed > 0 ? ` and ${signed} signed card${signed === 1 ? '' : 's'}` : ''
            }${
              bets > 0 ? ` and ${bets} side match${bets === 1 ? '' : 'es'} struck over it` : ''
            }. Removing it orphans ${signed > 0 ? 'them and unsettles that money' : 'them'}${
              bets > 0 ? ', and those bets will stop settling' : ''
            } — and re-adding the round will not bring them back. Tap again if that is really what you want.`,
      )
      return
    }
    setArmedRound(null)
    setDraft({
      ...draft,
      matches: draft.matches.filter((m) => m.roundId !== id),
      groups: draft.groups.filter((g) => g.roundId !== id),
      pairings: draft.pairings.filter((p) => p.groupId === '' || !draft.groups.some((g) => g.roundId === id && g.id === p.groupId)),
      /*
       * The frozen handicaps go with it. Left behind they are rows for a round
       * that no longer exists, which `staleFrozenRounds` then reports as
       * differing from the roster forever — with no row in the Handicaps list
       * to fix, because that list iterates the rounds.
       */
      handicaps: draft.handicaps.filter((h) => h.roundId !== id),
      // And the renumber, so the week does not read 1, 2, 4, 5.
      rounds: inPlayOrder(draft.rounds.filter((r) => r.id !== id)),
    })
  }

  // --- the draw ------------------------------------------------------------

  const drawPairings = () => {
    /*
     * Regenerating the draw mints NEW group ids, and signatures key on
     * roundId~groupId — so with anything signed, this button un-settles every
     * signed match in one tap while looking like housekeeping. Armed rather
     * than forbidden: a genuine mid-week redraw (injury, departure) is real,
     * but it has to be chosen with the cost in front of it.
     */
    /*
     * Always twice, not only when something is signed.
     *
     * The old rule armed this on signatures alone, so before the week starts it
     * was one tap that silently threw away a draw somebody may have spent an
     * evening editing by hand — and the whole point of the seating editor is
     * that hand edits are now worth keeping. The cost is different in the two
     * cases and the message says which one you are paying.
     */
    if (!armedRedraw) {
      setArmedRedraw(true)
      setNote(
        liveConfirmations.length > 0
          ? `${liveConfirmations.length} card${liveConfirmations.length === 1 ? ' is' : 's are'} signed. A new draw mints new group ids and orphans every one of them — settled matches will UNSETTLE. Tap again if that is really what you want.`
          : 'A new draw replaces every foursome in the week, including any you have edited by hand. Tap again to redraw.',
      )
      return
    }
    setArmedRedraw(false)
    const players = draft.players
      .filter((p) => p.active)
      .map((p) => {
        const course = draft.courses[0]!
        const tee = findTee(course, p)
        return {
          id: p.id,
          courseHandicap: courseHandicapFor(playingIndex(p), tee, course.holes),
        }
      })

    /*
     * The draw only knows how to build foursomes.
     *
     * Every format here is played four to a group — two singles inside it, or
     * a 2v2 — and the optimiser's matchings are hard-coded to four. Handed
     * eleven players it used to build a threesome, reach for its fourth man and
     * throw. React does not route an error from an event handler to the error
     * boundary, so the button simply did nothing: no message, no console, no
     * hint. That is the morning somebody's flight is canceled.
     */
    if (players.length === 0) {
      setNote('Nobody is playing — add players before drawing.')
      return
    }
    if (players.length % 4 !== 0) {
      const over = players.length % 4
      setNote(
        `The draw needs whole foursomes — you have ${players.length} players, which is ${over} too many for ${Math.floor(players.length / 4)} groups. Add ${4 - over} or remove ${over}.`,
      )
      return
    }

    /*
     * A foursome that goes out together stays together.
     *
     * The nines are separate MATCHES but they are one round of golf: you play
     * the front against one man, walk to the 10th, and play the back against
     * another — without ever leaving the group. Drawing each nine independently
     * would split a foursome halfway round, which is not a thing that can
     * physically happen.
     *
     * So a round that follows another on the same course on the same day reuses
     * its groups and is only required to re-pair inside them. Everything else
     * is drawn fresh, and the optimizer still spreads partners and opponents
     * across the week as usual.
     */
    /*
     * The week's own rounds, and not the ones somebody added on a tee.
     *
     * A draw rebuilds every foursome it is given. An evening nine that six men
     * played would be re-seated as three twelve-man foursomes of people who
     * were never there, and its groups would take fresh ids — orphaning its
     * card, since a signature keys on roundId~groupId. It also has no business
     * in the optimiser's exposure sums: it is not part of the week being
     * balanced.
     */
    /*
     * Every round added on a tee, not merely the ones played for fun.
     *
     * This filtered on `counts === false`, which misses the case the feature
     * exists for: one of the four named men adds a nine that COUNTS, eight men
     * play it, three sign it — and the next redraw re-seats it with all twelve,
     * mints new group ids, and the signatures come loose from their groups.
     * Signed cards are money, and they would have gone silently.
     *
     * `counts` is the marker because the server always writes it explicitly on
     * a round created from a phone, true or false, and no scheduled round has
     * ever carried the field.
     */
    const adhoc = new Set(
      draft.matches.filter((m) => m.counts !== undefined).map((m) => m.roundId),
    )
    const ordered = [...draft.rounds]
      .filter((r) => !adhoc.has(r.id))
      .sort((a, b) => a.seq - b.seq)
    const specs: RoundSpec[] = ordered.map((r, i) => {
      const match = draft.matches.find((m) => m.roundId === r.id)
      const kind = match?.config.kind ?? 'quota'
      const prev = ordered[i - 1]
      const sameOuting = prev && prev.courseId === r.courseId && prev.date === r.date
      return {
        id: r.id,
        type: roundTypeFor(kind),
        ...(sameOuting ? { reuseGroupsFrom: prev.id, mustDifferFrom: prev.id } : {}),
        balanceHandicaps: kind === 'quota' && match?.config.kind === 'quota'
          ? match.config.rankingScope === 'group'
          : false,
      }
    })

    /*
     * Belt and braces on the optimiser itself.
     *
     * The foursome check above catches the failure that actually happened, but
     * anything else that throws in here lands in the same silent hole — an
     * event handler, so the boundary never sees it. A message nobody expected
     * beats a button that does nothing.
     */
    let plan: ReturnType<typeof optimize>['plan']
    try {
      ;({ plan } = optimize(specs, players, {
        seed: `${draft.settings.rngSeed}#${draft.settings.reshuffleCounter}`,
      }))
    } catch (e) {
      setNote(
        `The draw could not be built: ${e instanceof Error ? e.message : 'unknown error'}. Check the rounds and formats, then try again.`,
      )
      return
    }

    const groups: Group[] = []
    const pairings: Pairing[] = []

    plan.rounds.forEach((planned) => {
      const round = draft.rounds.find((r) => r.id === planned.roundId)!
      const match = draft.matches.find((m) => m.roundId === round.id)
      planned.groups.forEach((g, i) => {
        const [h, m] = round.firstTeeTime.split(':').map(Number)
        const minutes = (h ?? 9) * 60 + (m ?? 0) + i * round.teeIntervalMinutes
        const groupId = uid()
        // A flighted round's groups ARE the flights: the first slot drawn is
        // Flight 1, and the number then rides with the group through reorders.
        const flighted = match?.config.kind === 'strokeNet' && match.config.flighted
        groups.push({
          id: groupId,
          roundId: round.id,
          index: i,
          teeTime: `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`,
          playerIds: g.playerIds,
          pinned: false,
          ...(flighted ? { flight: i + 1 } : {}),
        })

        if (!match) return
        const kind = match.config.kind
        if (kind === 'individualMatchPlay') {
          // Two singles matches inside the foursome.
          contestsFor(g)?.forEach(([a, b], idx) =>
            pairings.push({
              id: uid(),
              matchId: match.id,
              groupId,
              index: idx,
              sides: [
                { id: `${groupId}-${idx}-a`, playerIds: [a] },
                { id: `${groupId}-${idx}-b`, playerIds: [b] },
              ],
              pinned: false,
            }),
          )
        } else if (
          kind === 'teamHighLow' ||
          kind === 'teamCombinedNet' ||
          kind === 'teamFourBall'
        ) {
          const sides = sidesFor(g)
          if (sides)
            pairings.push({
              id: uid(),
              matchId: match.id,
              groupId,
              index: 0,
              sides: [
                { id: `${groupId}-a`, playerIds: sides[0] },
                { id: `${groupId}-b`, playerIds: sides[1] },
              ],
              pinned: false,
            })
        }
      })
    })

    // Everything the draw did not touch is carried over untouched.
    groups.push(...draft.groups.filter((g) => adhoc.has(g.roundId)))
    const adhocMatchIds = new Set(
      draft.matches.filter((m) => adhoc.has(m.roundId)).map((m) => m.id),
    )
    pairings.push(...draft.pairings.filter((p) => adhocMatchIds.has(p.matchId)))

    const report = fairnessReport(plan, specs, players)
    setNote(
      report.neverTogether.length === 0
        ? `Drawn. Every pair shares a group between ${report.minTogether} and ${report.maxTogether} times.`
        : `Drawn, but ${report.neverTogether.length} pairs never meet — add more rounds.`,
    )
    setDraft({ ...draft, groups, pairings })
  }

  /**
   * Send a foursome out earlier or later.
   *
   * Only the running order moves. The optimiser's job is who plays with whom,
   * and that is untouched — so wanting a particular group off first costs none
   * of the exposure balancing it worked out. Tee times are recomputed from the
   * round's first time and interval rather than swapped, so the sequence stays
   * evenly spaced however many times this is used.
   */
  const moveGroup = (roundId: string, from: number, delta: number) => {
    const round = draft.rounds.find((r) => r.id === roundId)
    if (!round) return
    const inRound = draft.groups
      .filter((g) => g.roundId === roundId)
      .sort((a, b) => a.index - b.index)
    const to = from + delta
    if (to < 0 || to >= inRound.length) return

    const order = [...inRound]
    const [moved] = order.splice(from, 1)
    order.splice(to, 0, moved!)

    const [h, m] = round.firstTeeTime.split(':').map(Number)
    const start = (h ?? 9) * 60 + (m ?? 0)
    const retimed = new Map(
      order.map((g, i) => {
        const minutes = start + i * round.teeIntervalMinutes
        return [
          g.id,
          {
            index: i,
            teeTime: `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(
              minutes % 60,
            ).padStart(2, '0')}`,
          },
        ]
      }),
    )

    setDraft({
      ...draft,
      groups: draft.groups.map((g) => {
        const patch = retimed.get(g.id)
        return patch ? { ...g, ...patch } : g
      }),
    })
  }

  // --- editing a foursome by hand -----------------------------------------

  /**
   * What a man gets round THIS round — his course handicap off his tee, over
   * the holes this round plays.
   *
   * Shown beside every name while a foursome is being edited, because moving a
   * man is never only about who he plays with: three of the four groups can be
   * level and the fourth carry forty strokes, and there is no way to see that
   * from a list of names.
   */
  const strokesIn = (round: Round) => {
    const course = draft.courses.find((c) => c.id === round.courseId)
    return (id: string): number => {
      const p = draft.players.find((x) => x.id === id)
      if (!p || !course) return 0
      return courseHandicapFor(playingIndex(p), findTee(course, p), holesFor(course, round.nine))
    }
  }

  /**
   * Open a round's foursomes, saying first what an edit will cost.
   *
   * The cost is genuinely smaller than a redraw's and it is not nothing, so it
   * is stated in the terms that are true: the group ids do not move, so nothing
   * comes loose from its signature — but every number in this app is derived,
   * so a settled match re-settles against whoever is in the group now. Armed
   * the way `removeRound` and `drawPairings` are armed, and for the same reason.
   */
  const openSeating = (roundId: string) => {
    if (seatingRound === roundId) {
      setSeatingRound(null)
      setPicked(null)
      return
    }
    const signed = liveConfirmations.filter((c) => c.roundId === roundId).length
    const played = new Set(
      liveScores.filter((sc) => sc.roundId === roundId).map((sc) => `${sc.playerId}~${sc.holeNo}`),
    ).size
    if ((signed > 0 || played > 0) && armedSeating !== roundId) {
      const round = draft.rounds.find((r) => r.id === roundId)
      setArmedSeating(roundId)
      setNote(
        `${round?.label ?? 'This round'} has ${played} score${played === 1 ? '' : 's'} on it${
          signed > 0 ? ` and ${signed} signed card${signed === 1 ? '' : 's'}` : ''
        }. Moving a man keeps every group id, so nothing comes loose from its signature — but the standings and the ledger are worked out from who is in each group, so ${
          signed > 0 ? 'a settled match will re-settle' : 'the scoring will re-decide'
        } against the new names. Tap again to edit the foursomes.`,
      )
      return
    }
    setArmedSeating(null)
    setPicked(null)
    setSeatingRound(roundId)
  }

  /**
   * Tapping a name: pick him up, put him down, or trade him with the man tapped
   * second.
   *
   * A trade rather than a move, when both men are seated, because that is what
   * the group asks for nine times out of ten and it cannot leave a fivesome
   * behind. Moving one man on his own is the group headers' job — see "Move
   * here" — and that is the drop-out case, which genuinely does want a
   * threesome for a moment.
   */
  const tapMan = (roundId: string, playerId: string) => {
    if (picked === null || picked === playerId) {
      setPicked(picked === playerId ? null : playerId)
      return
    }
    const from = groupOf(draft, roundId, picked)
    const to = groupOf(draft, roundId, playerId)
    // Same group, or one of them is not seated at all: nothing sensible to
    // trade, so the second tap simply picks the second man up instead.
    if (!from || !to || from.id === to.id) {
      setPicked(playerId)
      return
    }
    setDraft(swapSeats(draft, roundId, picked, playerId))
    setPicked(null)
  }

  /**
   * Pair the round's foursomes off again, in place.
   *
   * The other half of clearing the contests. Without it the only way back to a
   * scoreable round is "Generate pairings", which rebuilds the whole week and
   * mints new group ids — the exact thing this section exists to avoid.
   */
  const repair = (round: Round) => {
    const next = redrawContests(draft, round.id, strokesIn(round))
    if (next === draft) {
      setNote('Nothing to pair off — every foursome here already has its contests.')
      return
    }
    setDraft(next)
    setNote(
      `${round.label} paired off again — low with high in each foursome. The group ids and the tee times are untouched.`,
    )
  }

  /*
   * A reshuffle is a redraw with a delay on it.
   *
   * It only bumps the seed, so nothing moves until "Generate pairings" — which
   * made it feel free, and it is not: it throws away the draw everybody has
   * already been told about the moment the next tap lands. Asked for twice, in
   * the same words as everything else here.
   */
  const reshuffle = () => {
    if (!armedShuffle) {
      setArmedShuffle(true)
      setNote(
        'A reshuffle changes the seed the draw is built from, so the next "Generate pairings" gives a different week — different foursomes, different opponents. Tap again to reshuffle.',
      )
      return
    }
    setArmedShuffle(false)
    setDraft({
      ...draft,
      settings: {
        ...draft.settings,
        reshuffleCounter: draft.settings.reshuffleCounter + 1,
      },
    })
    setNote(`Draw #${draft.settings.reshuffleCounter + 1} seeded. Tap "Generate pairings" to build it.`)
  }

  const unverified = draft.courses.filter((c) => !c.verified)

  return (
    <>
      <Bar title="Setup" sub={`${draft.name} · ${draft.venue}`} />
      {unverified.length > 0 && <UnverifiedBanner courseName={unverified[0]!.name} />}

      {note && <div className="bg-ink text-paper px-4 py-2 bc-label">{note}</div>}

      {/*
        Auto-save has to be visible or it is indistinguishable from not saving.
        Sticky, because the answer to "did that take?" is wanted at the moment
        of doubt, which is not the moment the edit was made.
      */}
      <div className="px-4 py-1.5 border-b border-rule flex items-center justify-between gap-3">
        <span className="bc-label text-mute">Setup</span>
        <span className="text-[12px] text-mute">
          {saving ? 'Saving…' : dirty ? 'Unsaved changes' : 'All changes saved'}
        </span>
      </div>

      {/*
        The four shelves. Same clothes as the Odds sub-tabs, so nothing new
        to learn: Golf is the mid-week workbench and the default; Field is
        who is playing, off what, and whether they have paid; Trip is the
        bookkeeping that is edited before the week and read rarely after;
        Run is starting, the money, and the irreversible. Badges only when
        a shelf is owed something.
      */}
      {(() => {
        const active = draft.players.filter((p) => p.active)
        const paid = active.filter((p) => p.buyInPaidAt).length
        const owed = active.length - paid
        const toFix = startProblems(draft).length
        const label = {
          golf: 'Golf',
          field: owed > 0 ? `Field (${owed})` : 'Field',
          trip: unverified.length > 0 ? `Trip (${unverified.length})` : 'Trip',
          run: toFix > 0 ? `Run (${toFix})` : 'Run',
        }
        return (
          <div className="grid grid-cols-4 border-b border-rule">
            {(['golf', 'field', 'trip', 'run'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setSection(t)}
                className={`bc-label py-3 bc-tap ${
                  section === t ? 'bg-ink text-paper' : 'bg-paper text-mute'
                }`}
              >
                {label[t]}
              </button>
            ))}
          </div>
        )
      })()}

      {section === 'trip' && (
        <>
      {draft.courses.length === 0 && (
        <div className="p-4 border-b border-rule">
          <p className="text-[13px] text-mute leading-snug mb-2">
            No courses loaded. The Bandon cards are transcribed from public scorecard
            databases, not from the physical card — check them before anyone tees off.
          </p>
          <button
            type="button"
            onClick={() => setDraft({ ...draft, courses: BANDON_COURSES })}
            className="bc-tap w-full border-2 border-ink bc-label py-3"
          >
            Load the Bandon courses
          </button>
          {/* The Idaho cards, so a past year can be reconstructed rather than
              only remembered — a tournament without its courses cannot work out
              a single pop. */}
          <button
            type="button"
            onClick={() => setDraft({ ...draft, courses: IDAHO_COURSES })}
            className="bc-tap w-full border-2 border-ink bc-label py-3 mt-2"
          >
            Load the Idaho courses (2024 &amp; 2025)
          </button>
        </div>
      )}

      {/*
        The facts about the tournament itself.
        
        None of these could be edited once a tournament existed — venue, dates
        and buy-in were fixed at creation, and the payouts screen printed "Pot at
        $750 a head" from a number nobody could change. The dates are not
        cosmetic either: they decide which tournament the bare domain unfurls as,
        and they are what a new round's default date counts from.
      */}
      <SectionRule>The tournament</SectionRule>
      <div className="px-4 py-3 grid gap-3 border-b border-rule">
        <label className="grid gap-1">
          <span className="bc-label text-mute">Name</span>
          <input
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            className="bc-field bc-name bc-tap"
          />
        </label>
        <label className="grid gap-1">
          <span className="bc-label text-mute">Venue</span>
          <input
            value={draft.venue}
            onChange={(e) => setDraft({ ...draft, venue: e.target.value })}
            placeholder="Bandon Dunes"
            className="bc-field bc-name bc-tap"
          />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="grid gap-1">
            <span className="bc-label text-mute">First day</span>
            <input
              type="date"
              value={draft.startDate}
              onChange={(e) => setDraft({ ...draft, startDate: e.target.value })}
              className="bc-field bc-num bc-tap"
            />
          </label>
          <label className="grid gap-1">
            <span className="bc-label text-mute">Last day</span>
            <input
              type="date"
              value={draft.endDate}
              onChange={(e) => setDraft({ ...draft, endDate: e.target.value })}
              className="bc-field bc-num bc-tap"
            />
          </label>
        </div>
        {draft.endDate && draft.startDate && draft.endDate < draft.startDate && (
          <p className="bg-signal text-paper px-3 py-2 text-xs leading-snug">
            The last day is before the first. Fix this — the dates decide which
            tournament the shared link points at.
          </p>
        )}
        <div className="grid grid-cols-2 gap-3">
          <label className="grid gap-1">
            <span className="bc-label text-mute">Buy-in</span>
            <input
              type="number"
              inputMode="numeric"
              min={0}
              value={draft.settings.buyIn}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  settings: { ...draft.settings, buyIn: Math.max(0, Number(e.target.value) || 0) },
                })
              }
              className="border-2 border-ink px-3 py-2 bc-num bc-tap bg-paper"
            />
          </label>
          <label className="grid gap-1">
            <span className="bc-label text-mute">Venmo</span>
            <input
              value={draft.settings.venmoHandle}
              onChange={(e) =>
                setDraft({ ...draft, settings: { ...draft.settings, venmoHandle: e.target.value } })
              }
              placeholder="@handle"
              className="bc-field bc-name bc-tap"
            />
          </label>
        </div>

        {/*
          Free text, one fact per line, because that is how it arrives.
          The flight, the food and the ride home turn up in the group thread a
          few at a time over months. Anything more structured would need a new
          field for every message.
        */}
        <label className="grid gap-1">
          <span className="bc-label text-mute">Getting there</span>
          <textarea
            rows={6}
            value={draft.settings.travel ?? ''}
            onChange={(e) =>
              setDraft({ ...draft, settings: { ...draft.settings, travel: e.target.value } })
            }
            placeholder={'Wheels up 12:30\nOut of Atlantic Aviation\nLunch on the plane'}
            className="bc-field leading-snug bc-tap"
          />
          <span className="text-[13px] text-mute leading-snug">
            One line each. These sit at the top of The week, above the golf, where
            everybody will look for them the night before.
          </span>
        </label>

        {/*
          The day's own logistics — dinner, massages, the departure clock.
          One small text block per scheduled day, dates taken from the rounds
          themselves so there is nothing to pick and nothing to mistype. Same
          free-text rule as "Getting there": these arrive in the thread a few
          at a time, and a schema would turn next week's message into a
          migration. Shown INSIDE that day on The week, under the weather.
        */}
        <div className="grid gap-3">
          <span className="bc-label text-mute">Each day, on the day</span>
          {[...new Set(inPlayOrder(draft.rounds).map((r) => r.date))].map((date) => (
            <div key={date} className="grid gap-1">
              <label className="grid gap-1">
              <span className="text-[13px] text-mute">{weekdayOf(date)} · {date.slice(5)}</span>
              <textarea
                rows={2}
                value={draft.settings.dayNotes?.[date] ?? ''}
                onChange={(e) => {
                  const next = { ...(draft.settings.dayNotes ?? {}) }
                  if (e.target.value.trim() === '') delete next[date]
                  else next[date] = e.target.value
                  setDraft({ ...draft, settings: { ...draft.settings, dayNotes: next } })
                }}
                placeholder={'8:00pm — Dinner'}
                className="bc-field leading-snug bc-tap"
              />
              </label>
              {/* Rendered inside a fragment-shaped div below the label, or a
                  tap on any slot control would ALSO activate the label and
                  yank focus into the notes textarea. */}
              {/*
                Massage slots, structured where the notes are prose: a slot is
                a time and the man in it, and those two facts must not come
                apart. Leaving the player unset keeps the slot OPEN, which the
                week view says out loud — "open" answers "can I still get one"
                without a text to the thread.
              */}
              {(() => {
                const slots = draft.settings.massages?.[date] ?? []
                const write = (next: { time: string; playerId: string | null }[]) => {
                  const all = { ...(draft.settings.massages ?? {}) }
                  if (next.length === 0) delete all[date]
                  else all[date] = next
                  setDraft({ ...draft, settings: { ...draft.settings, massages: all } })
                }
                return (
                  <div className="grid gap-1.5">
                    {slots.map((slot, i) => (
                      <div key={i} className="flex gap-2 items-stretch">
                        {/*
                          Two selects, not a native time input — iOS ignores
                          `step` on a time input outright and hands the admin
                          a sixty-stop minute wheel however politely we asked
                          for fives. Selects render as native pickers with
                          exactly the options given, so the minutes ARE fives.
                        */}
                        {(() => {
                          const [hh = '07', mm = '00'] = (slot.time || '07:00').split(':')
                          const setTime = (h: string, m: string) =>
                            write(
                              slots.map((x, j) => (j === i ? { ...x, time: `${h}:${m}` } : x)),
                            )
                          const hours = Array.from({ length: 16 }, (_, n) =>
                            String(n + 5).padStart(2, '0'),
                          )
                          const minutes = Array.from({ length: 12 }, (_, n) =>
                            String(n * 5).padStart(2, '0'),
                          )
                          const label = (h: string) => {
                            const n = Number(h)
                            return `${((n + 11) % 12) + 1} ${n < 12 ? 'AM' : 'PM'}`
                          }
                          return (
                            <>
                              <select
                                value={hh}
                                onChange={(e) => setTime(e.target.value, mm)}
                                aria-label="Hour"
                                className="bc-field bc-num bc-tap"
                              >
                                {[...new Set([...hours, hh])].sort().map((h) => (
                                  <option key={h} value={h}>
                                    {label(h)}
                                  </option>
                                ))}
                              </select>
                              <select
                                value={mm}
                                onChange={(e) => setTime(hh, e.target.value)}
                                aria-label="Minutes"
                                className="bc-field bc-num bc-tap"
                              >
                                {[...new Set([...minutes, mm])].sort().map((m) => (
                                  <option key={m} value={m}>
                                    :{m}
                                  </option>
                                ))}
                              </select>
                            </>
                          )
                        })()}
                        <select
                          value={slot.playerId ?? ''}
                          onChange={(e) =>
                            write(
                              slots.map((x, j) =>
                                j === i ? { ...x, playerId: e.target.value || null } : x,
                              ),
                            )
                          }
                          className="border-2 border-ink px-2 bc-tap bg-paper flex-1 min-w-0 text-sm"
                        >
                          <option value="">— open —</option>
                          {draft.players
                            .filter((pl) => pl.active)
                            .map((pl) => (
                              <option key={pl.id} value={pl.id}>
                                {pl.displayName}
                              </option>
                            ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => write(slots.filter((_, j) => j !== i))}
                          aria-label="Remove this slot"
                          className="bc-tap px-3 border-2 border-ink bg-paper"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() =>
                        write([
                          ...slots,
                          {
                            // Twenty past the previous slot, or the morning's
                            // first chair — the sheet fills itself forward.
                            time: (() => {
                              const prev = slots[slots.length - 1]?.time
                              if (!prev) return '07:00'
                              const [h, m] = prev.split(':').map(Number)
                              // A hand-edited doc can hold "7:00 PM"; NaN
                              // arithmetic would store a NaN:NaN slot.
                              if (!Number.isFinite(h) || !Number.isFinite(m)) return '07:00'
                              const mins = Math.min(h! * 60 + m! + 20, 20 * 60)
                              return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`
                            })(),
                            playerId: null,
                          },
                        ])
                      }
                      className="bc-tap bc-label border border-rule px-3 py-2 text-left"
                    >
                      + Massage slot
                    </button>
                  </div>
                )
              })()}
            </div>
          ))}
        </div>
        <p className="text-[13px] text-mute leading-snug">
          The pot is the buy-in times the number of active players, and the payouts
          are checked against it under Run. Changing the buy-in does not move any payout
          — it changes what they are being compared to.
        </p>
      </div>

      {draft.courses.length > 0 && (
        <>
          {/*
            Collapsed by default once every card is checked.
            
            Five courses, each with a paragraph explaining where its numbers came
            from, was most of the Setup screen — and all of it is reference
            material that matters once, before the first tee shot, and never
            again. It opens itself when something is unchecked, because that is
            the one state where it needs to be read.
          */}
          <button
            type="button"
            onClick={() => setCoursesOpen((v) => !v)}
            className="block w-full text-left bc-label border-t-[3px] border-ink pt-3 pb-2 mt-5 px-4 flex items-baseline justify-between gap-3 bc-tap"
          >
            <span>Courses</span>
            <span className="normal-case tracking-normal text-[12px]">
              {(() => {
                const unchecked = draft.courses.filter((c) => !c.verified).length
                return unchecked > 0 ? (
                  <span className="text-signal">
                    {unchecked} unchecked{coursesOpen ? '' : ' — tap to open'}
                  </span>
                ) : (
                  <span className="text-mute">
                    {draft.courses.length} checked {coursesOpen ? '▴' : '▾'}
                  </span>
                )
              })()}
            </span>
          </button>
          {coursesOpen && (
          <>
          {[...BANDON_COURSES, ...IDAHO_COURSES].some(
            (c) => !draft.courses.some((x) => x.id === c.id),
          ) && (
            <div className="px-4 pt-2">
              <button
                type="button"
                onClick={() =>
                  setDraft({
                    ...draft,
                    courses: [
                      ...draft.courses,
                      ...[...BANDON_COURSES, ...IDAHO_COURSES].filter(
                        (c) => !draft.courses.some((x) => x.id === c.id),
                      ),
                    ],
                  })
                }
                className="bc-tap w-full border-2 border-ink bc-label py-3"
              >
                Add the cards this tournament is missing
              </button>
            </div>
          )}
          <p className="px-4 pt-1 pb-2 text-[13px] text-mute leading-snug">
            Cards are transcribed from public databases, which is better than a guess
            and worse than the real thing. Tick one off once someone has held the
            physical card next to it — that clears the banner for that course.
          </p>
          <ul className="divide-y divide-rule">
            {draft.courses.map((c) => (
              <li key={c.id} className="px-4 py-3">
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={c.verified}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        courses: draft.courses.map((x) =>
                          x.id === c.id ? { ...x, verified: e.target.checked } : x,
                        ),
                      })
                    }
                    className="mt-0.5 w-5 h-5 accent-[var(--color-ink)] bc-tap shrink-0"
                  />
                  <span className="min-w-0">
                    <span className="bc-name text-sm flex items-center gap-2">
                      {c.name}
                      {!c.verified && (
                        <span className="bg-signal text-paper text-[11px] font-extrabold px-1 py-0.5">
                          UNCHECKED
                        </span>
                      )}
                    </span>
                  </span>
                </label>

                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => {
                      const closing = openCourse === c.id
                      setOpenCourse(closing ? null : c.id)
                      // Hiding the card ends the edit too — a card that reopens
                      // as a wall of inputs looks like it wants retyping.
                      if (closing) setEditCourse(null)
                    }}
                    className="bc-label text-mute bc-tap mt-1"
                  >
                    {openCourse === c.id ? 'Hide the card' : 'Show the card'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (editCourse === c.id) {
                        setEditCourse(null)
                      } else {
                        setEditCourse(c.id)
                        setOpenCourse(c.id)
                      }
                    }}
                    className="bc-label text-mute bc-tap mt-1"
                  >
                    {editCourse === c.id ? 'Done editing' : 'Edit the card'}
                  </button>
                </div>

                {openCourse === c.id && (
                  <>
                    {/*
                      The photograph beside the derived card, because this screen
                      is where the two get compared. The derived card is what the
                      app scores from; the photo is what it was checked against —
                      and while editing it is the thing being copied FROM.
                    */}
                    <div className="flex mt-2">
                      <CardPhotoButton courseId={c.id} courseName={c.name} />
                    </div>
                    {editCourse === c.id ? (
                      <CourseEditor
                        course={c}
                        players={draft.players}
                        rescoresPlayedGolf={liveScores.length > 0 || liveConfirmations.length > 0}
                        onChange={(next) =>
                          setDraft({
                            ...draft,
                            courses: draft.courses.map((x) => (x.id === c.id ? next : x)),
                          })
                        }
                      />
                    ) : (
                      <CourseCard course={c} />
                    )}
                    {/* Where the numbers came from, next to the numbers. */}
                    {c.notes && (
                      <p className="text-[13px] text-mute leading-snug mt-2">{c.notes}</p>
                    )}
                  </>
                )}
              </li>
            ))}
          </ul>
          </>
          )}
        </>
      )}
        </>
      )}

      {section === 'field' && (
        <>
      {/*
        The buy-in ledger. Scott is the one taking the money, so this is his
        checklist: one tap marks a man in, and the row keeps the date because
        "when?" is always the next question. Un-marking follows the same
        two-press rule as removing a round — saying the money is NOT there,
        after everybody saw it marked in, should cost a deliberate second tap.
      */}
      <div className="flex items-end justify-between gap-3 mx-4 mt-5 mb-1 border-t-[3px] border-ink pt-2">
        <h2 className="bc-label">The buy-in</h2>
        <span className="bc-label text-mute">
          {draft.players.filter((p) => p.active && p.buyInPaidAt).length} of{' '}
          {draft.players.filter((p) => p.active).length} in
        </span>
      </div>
      <ul className="divide-y divide-rule border-t border-rule">
        {draft.players
          .filter((p) => p.active)
          .map((p) => {
            const paid = Boolean(p.buyInPaidAt)
            const armed = armedUnpaid === p.id
            const when = p.buyInPaidAt
              ? new Date(p.buyInPaidAt).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                })
              : null
            return (
              <li key={p.id} className="flex items-center justify-between gap-2 px-4 py-2">
                <span className="min-w-0">
                  <span className="bc-name text-sm truncate block">{p.displayName}</span>
                  {when && <span className="text-[12px] text-mute block">in {when}</span>}
                </span>
                <button
                  type="button"
                  onBlur={() => armed && setArmedUnpaid(null)}
                  onClick={() => {
                    if (!paid) {
                      setBuyIn(p.id, new Date().toISOString())
                    } else if (!armed) {
                      setArmedUnpaid(p.id)
                    } else {
                      setArmedUnpaid(null)
                      setBuyIn(p.id, null)
                    }
                  }}
                  aria-label={
                    paid
                      ? `${p.displayName} paid — tap twice to un-mark`
                      : `Mark ${p.displayName} paid`
                  }
                  className={`bc-tap bc-label px-3 shrink-0 ${
                    armed
                      ? 'bg-signal text-paper'
                      : paid
                        ? 'bg-ink text-paper'
                        : 'border-2 border-ink'
                  }`}
                >
                  {armed ? "Tap again — hasn't paid?" : paid ? 'Paid \u2713' : 'Mark paid'}
                </button>
              </li>
            )
          })}
      </ul>

      <div className="flex items-end justify-between gap-3 mx-4 mt-5 mb-1 border-t-[3px] border-ink pt-2">
        <h2 className="bc-label">Players &amp; handicaps</h2>
        <button
          type="button"
          onClick={() => setRosterEdit((v) => !v)}
          className="bc-tap bc-label px-3 py-1.5 border-2 border-ink"
        >
          {rosterEdit ? 'Done' : 'Edit'}
        </button>
      </div>

      {/*
        Read first, edit on purpose.
        Twelve rows of always-live inputs made the roster look like a form that
        wanted filling in, when nearly every visit to this screen is somebody
        checking a number rather than changing one. The numbers are also now
        looked up in GHIN rather than invented, so the common case is reading
        them back to confirm they arrived.
      */}
      <div className="grid grid-cols-[minmax(0,1fr)_2.75rem_2.75rem_1.75rem] gap-1.5 items-end px-4 pb-1 pt-2">
        <span className="bc-label text-mute">Player</span>
        <span className="bc-label text-mute text-center">Index</span>
        <span className="bc-label text-mute text-center">Low</span>
        <span className="bc-label text-mute text-right">CH</span>
      </div>

      {!rosterEdit && (
        <ul className="divide-y divide-rule border-t border-rule">
          {draft.players.map((p) => {
            const course = draft.courses[0]
            const tee = course ? findTee(course, p) : null
            /*
             * How many DIFFERENT tees he is down for across the week.
             *
             * Worth showing in the closed row, because the failure this screen
             * had was silent: one dropdown for the first course only, and seven
             * rounds quietly scored off the tips. "Green" told you nothing about
             * the other four cards; "Green on all five" does.
             */
            /*
             * Only the courses the week actually plays.
             *
             * A course in the document that no round uses — the Preserve is
             * carried for a nine somebody might add on the day — still has a
             * tee, and it appeared in every one of the twelve roster rows as
             * "Green / The Preserve". A man's tees are worth summarising for
             * the golf he is playing.
             */
            const played = draft.courses.filter((c) =>
              draft.rounds.some((r) => r.courseId === c.id),
            )
            const teeNames = [...new Set(played.map((c) => findTee(c, p).name))]
            const ch =
              course && tee
                ? courseHandicapFor(playingIndex(p), tee, holesFor(course, 'full18'))
                : 0
            const onLow = p.playsOff === 'low' && typeof p.lowIndex === 'number'
            /*
              The number in play is the one set in ink; the other is grayed.
              Both are always shown, because the whole point of holding two is
              the comparison — a man three shots above his low is the argument
              this screen exists to settle.
            */
            const live = 'bc-num text-sm text-center'
            const idle = 'bc-num text-sm text-center text-dim'
            return (
              <li
                key={p.id}
                className="grid grid-cols-[minmax(0,1fr)_2.75rem_2.75rem_1.75rem] gap-1.5 items-center px-4 py-2"
              >
                <span className="min-w-0">
                  <span className="bc-name text-sm truncate block">{p.displayName}</span>
                  <span className="text-[12px] text-mute truncate block">
                    {teeNames.length === 1
                      ? `${teeNames[0]} on all ${played.length}`
                      : teeNames.join(' / ')}
                    {onLow ? ' \u00b7 off his low' : ''}
                  </span>
                </span>
                <span className={onLow ? idle : live}>{p.handicapIndex.toFixed(1)}</span>
                <span className={onLow ? live : idle}>
                  {typeof p.lowIndex === 'number' ? p.lowIndex.toFixed(1) : '\u2014'}
                </span>
                <span className="bc-num text-sm text-right">{ch}</span>
              </li>
            )
          })}
        </ul>
      )}

      {rosterEdit && (
        <ul className="divide-y divide-rule border-t border-rule">
          {draft.players.map((p) => {

            return (
              <li key={p.id} className="px-4 py-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="bc-name text-sm truncate">{p.displayName}</span>
                  <button
                    type="button"
                    onClick={() => removePlayer(p.id)}
                    className="text-mute text-lg leading-none bc-tap px-2"
                    aria-label={`Remove ${p.displayName}`}
                  >
                    {'\u00d7'}
                  </button>
                </div>

                {/*
                  A row per player rather than a column per field. Editing wants
                  room that reading does not, and five controls squeezed across
                  393px was what made this screen read as a settings app.
                */}
                <div className="grid grid-cols-3 gap-1.5 mt-2">
                  <label className="min-w-0">
                    <span className="bc-label text-mute block mb-1">Index</span>
                    <input
                      type="number"
                      step="0.1"
                      inputMode="decimal"
                      value={p.handicapIndex}
                      onChange={(e) => setHandicap(p.id, Number(e.target.value))}
                      aria-label={`${p.displayName} handicap index`}
                      className="bc-field w-full text-center bc-num bc-tap"
                    />
                  </label>
                  <label className="min-w-0">
                    <span className="bc-label text-mute block mb-1">Low</span>
                    <input
                      type="number"
                      step="0.1"
                      inputMode="decimal"
                      value={p.lowIndex ?? ''}
                      placeholder="\u2014"
                      onChange={(e) => setLowIndex(p.id, e.target.value)}
                      aria-label={`${p.displayName} low handicap index, last twelve months`}
                      className="bc-field w-full text-center bc-num bc-tap"
                    />
                  </label>
                  <label className="min-w-0">
                    <span className="bc-label text-mute block mb-1">Plays off</span>
                    <select
                      value={p.playsOff ?? 'current'}
                      onChange={(e) => setPlaysOff(p.id, e.target.value as 'current' | 'low')}
                      disabled={typeof p.lowIndex !== 'number'}
                      aria-label={`${p.displayName} plays off`}
                      className="bc-field w-full bc-tap"
                    >
                      <option value="current">Index</option>
                      <option value="low">Low</option>
                    </select>
                  </label>
                </div>

                {/*
                  A tee per course, because there is a card per course.
                  This was one dropdown, wired to `courses[0]`, and the other
                  four cards had no control at all — so eleven men were set at
                  Bandon Dunes and playing the championship tips everywhere else,
                  with the course handicap on this very screen quietly wrong.
                  The star marks the tee his index suggests for that card.
                */}
                <div className="mt-2 border-t border-rule pt-2 flex flex-col gap-1.5">
                  <span className="bc-label text-mute">Tees, per course</span>
                  {draft.courses.map((c) => {
                    const t = findTee(c, p)
                    /*
                      The ★ never lands on a tee whose par is not believable.
                      The rule sends anybody over 18 to the SHORTEST tee, and the
                      short tees are exactly the ones still carrying the course
                      par — so the recommendation was worst where it was trusted
                      most. See `teeProblems`.
                    */
                    const suggested = recommendTee(playingIndex(p), c.tees, teesWithBadPar(c))
                    const suspect = teesWithBadPar(c)
                    const holes = holesFor(c, 'full18')
                    return (
                      <label
                        key={c.id}
                        className="grid grid-cols-[minmax(0,1fr)_5.5rem_2rem] gap-1.5 items-center"
                      >
                        <span className="text-[13px] truncate">{c.name}</span>
                        <select
                          value={t.id}
                          onChange={(e) => setTee(p.id, c.id, e.target.value)}
                          aria-label={`${p.displayName} tees at ${c.name}`}
                          className="bc-field bc-tap"
                        >
                          {c.tees.map((x) => (
                            <option key={x.id} value={x.id}>
                              {x.name}
                              {suggested?.id === x.id ? ' \u2605' : ''}
                              {suspect.has(x.id) ? ' (par unchecked)' : ''}
                            </option>
                          ))}
                        </select>
                        <span
                          className="bc-num text-[13px] text-right"
                          title={`Course handicap at ${c.name}`}
                        >
                          {courseHandicapFor(playingIndex(p), t, holes)}
                        </span>
                      </label>
                    )
                  })}
                </div>
              </li>
            )
          })}
        </ul>
      )}

      {rosterEdit && (
      <div className="flex gap-2 px-4 py-3">
        <input
          value={newPlayer}
          onChange={(e) => setNewPlayer(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              addPlayer()
            }
          }}
          placeholder="Add a player"
          className="bc-field flex-1 bc-name bc-tap"
        />
        <button
          type="button"
          onClick={addPlayer}
          disabled={!newPlayer.trim()}
          className="bc-tap bg-ink text-paper bc-label px-4 disabled:opacity-30"
        >
          Add
        </button>
      </div>
      )}

      <p className="px-4 pb-2 text-[13px] text-mute">
        {rosterEdit ? (
          <>
            ★ marks the suggested tee. CH is the course handicap that tee produces —
            it already accounts for playing a harder or easier set. “Plays off” chooses
            which of his two numbers decides his strokes, and is grayed out for anyone
            with no low index on file. A player added here can sign in as soon as you
            save; one removed can no longer sign in, and any round they were drawn into
            needs a new draw.
          </>
        ) : (
          <>
            Index is his current GHIN number, Low is his lowest of the last twelve
            months. Whichever he plays off is printed in black and the other in gray;
            CH is the strokes that gives him off his tee. Tap Edit to change any of it.
          </>
        )}
      </p>

        </>
      )}

      {section === 'golf' && (
        <>
      <SectionRule>Tee times, course &amp; format</SectionRule>
      {banner && (
        <div className="mx-4 mt-2 border-2 border-signal px-3 py-2 flex items-start gap-2">
          <p className="text-[13px] leading-snug flex-1">{banner}</p>
          <button
            type="button"
            onClick={() => setBanner(null)}
            aria-label="Dismiss"
            className="bc-tap bc-label px-2 py-1"
          >
            ×
          </button>
        </div>
      )}
      <ul className="divide-y divide-rule">
        {inPlayOrder(draft.rounds).map((r) => {
          const match = draft.matches.find((m) => m.roundId === r.id)
          const preset =
            FORMAT_PRESETS.find((f) => {
              const c = configFor(f.id)
              return (
                c.kind === match?.config.kind &&
                (c.kind !== 'quota' ||
                  (match.config.kind === 'quota' &&
                    c.rankingScope === match.config.rankingScope))
              )
            })?.id ?? 'individualMatchPlay'
          return (
            <li key={r.id} className="px-4 py-3 grid gap-2">
              <div className="flex gap-2 items-center">
                <input
                  value={r.label}
                  onChange={(e) => patchRound(r.id, { label: e.target.value })}
                  className="bc-name text-sm border-b border-rule flex-1 py-1 bc-tap"
                />
                <button
                  type="button"
                  onClick={() => removeRound(r.id)}
                  onBlur={() => armedRound === r.id && setArmedRound(null)}
                  className={`bc-label bc-tap px-2 ${
                    armedRound === r.id ? 'bg-signal text-paper' : 'text-signal'
                  }`}
                >
                  {armedRound === r.id ? 'Tap again' : 'Remove'}
                </button>
              </div>
              {/*
                The course gets its own row.

                Every control here renders at 16px — the iOS zoom guard in
                index.css is unlayered, so it beats the text-xs on these
                elements — while the columns were sized for 13px. "The Coeur
                d'Alene Resort Golf Course" ran clean under its own chevron.
                There were also four controls in a three-column grid, which
                dropped the interval onto a row of its own beside two empty
                cells.
              */}
              <div className="grid grid-cols-1 gap-2">
                {/*
                  The match number, and the only part of a match's name that is
                  typed rather than derived. A string because Match 6 is three
                  nines and they are 6a, 6b and 6c; blank on the championship,
                  which has never had one.
                */}
                <div className="grid grid-cols-[5.5rem_1fr] gap-2 items-center">
                  <input
                    type="text"
                    inputMode="text"
                    maxLength={4}
                    value={matchOf(r.id)?.number ?? matchNumber(matchOf(r.id) ?? { label: '' })}
                    onChange={(e) => patchMatchNumber(r.id, e.target.value)}
                    aria-label="Match number"
                    placeholder="No."
                    className="bc-field bc-tap text-center"
                  />
                  <span className="bc-name text-xs truncate">
                    {matchOf(r.id) ? matchNameIn(draft, matchOf(r.id)!) : 'No match on this round'}
                  </span>
                </div>
                <select
                  value={r.courseId}
                  onChange={(e) => patchRound(r.id, { courseId: e.target.value })}
                  className="bc-field bc-tap"
                >
                  {draft.courses.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <div className="grid grid-cols-3 gap-2">
                  <select
                  value={typeof r.nine === 'string' ? r.nine : 'full18'}
                  onChange={(e) => patchRound(r.id, { nine: e.target.value as Round['nine'] })}
                  className="bc-field bc-tap"
                >
                  <option value="front">Front 9</option>
                  <option value="back">Back 9</option>
                  <option value="full18">All 18</option>
                </select>
                <input
                  type="time"
                  value={r.firstTeeTime}
                  onChange={(e) => patchRound(r.id, { firstTeeTime: e.target.value })}
                  aria-label="First tee time"
                  className="bc-field bc-tap"
                />
                {/* Every round was locked to ten-minute intervals, which is a
                    starter's decision and not ours — and it is what the group's
                    tee times are computed from. */}
                <label className="flex items-center gap-1 text-xs text-mute min-w-0">
                  <input
                    type="number"
                    min={1}
                    max={30}
                    value={r.teeIntervalMinutes}
                    onChange={(e) =>
                      patchRound(r.id, {
                        teeIntervalMinutes: Math.min(30, Math.max(1, Number(e.target.value) || 10)),
                      })
                    }
                    aria-label="Minutes between tee times"
                    className="bc-field bc-tap w-14 bc-num text-center px-0"
                  />
                    <span className="whitespace-nowrap">min apart</span>
                </label>
                </div>
              </div>
              {/* Set the date; the day of the week is derived from it. Writing
                  "Monday" into the label is a second copy of the same fact, and
                  the copy is what goes stale when the schedule moves. */}
              <div className="flex items-center gap-2 mt-2">
                <input
                  type="date"
                  value={r.date}
                  onChange={(e) => patchRound(r.id, { date: e.target.value })}
                  className="bc-field bc-tap"
                />
                <span className="bc-label text-mute">{roundDay(r.date) || 'no date'}</span>
              </div>
              {/*
                The draw's rule, said out loud.

                Same course and same date means one outing, so the draw keeps
                the foursomes together across the front and the back. That is
                what the group wants and it was entirely invisible — which is
                how six rounds quietly drew the same four men every time. It is
                inferred from the two fields directly above it, so showing it
                here is the whole fix: change either and the line changes with
                it.
              */}
              {(() => {
                const ordered = [...draft.rounds].sort((a, b) => a.seq - b.seq)
                const i = ordered.findIndex((x) => x.id === r.id)
                const prev = ordered[i - 1]
                if (!prev || prev.courseId !== r.courseId || prev.date !== r.date) return null
                return (
                  <p className="text-[13px] text-signal leading-snug">
                    Same outing as <b className="bc-name">{prev.label}</b> — the draw keeps
                    these foursomes together. Change the course or the date to break them up.
                  </p>
                )
              })()}
              {match && (
                <select
                  value={preset}
                  onChange={(e) => setFormat(match.id, e.target.value as FormatPresetId)}
                  className="bc-field bc-name bc-tap"
                >
                  {FORMAT_PRESETS.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.label}
                    </option>
                  ))}
                </select>
              )}

              {match && (
                <details className="mt-2">
                  <summary className="bc-label text-mute bc-tap py-1 cursor-pointer">
                    What it pays
                  </summary>
                  <div className="pt-2 pl-1">
                    {/*
                      A ladder edited after the cards are in re-pays the match.
                      
                      Nothing here remembers what was paid — the ledger derives
                      from this config on every render — so raising a win from
                      $100 to $250 quietly hands six men $150 each on a round
                      that settled yesterday. Every other money-moving edit in
                      Setup says something first; this one was two taps behind a
                      disclosure and said nothing at all.
                    */}
                    {settledMatchIds.has(match.id) && (
                      <p className="border-2 border-signal px-3 py-2 text-[13px] leading-snug mb-2">
                        <b className="bc-name">This match is signed for and settled.</b>{' '}
                        Changing what it pays re-pays it — the standings and the ledger
                        recompute from these numbers, they do not remember the old ones.
                        Tell the group before you touch it.
                      </p>
                    )}
                    <PayoutEditor
                      match={match}
                      playerCount={draft.players.filter((p) => p.active).length}
                      onChange={(config) =>
                        setDraft({
                          ...draft,
                          matches: draft.matches.map((m) =>
                            m.id === match.id ? { ...m, config } : m,
                          ),
                        })
                      }
                    />
                  </div>
                </details>
              )}

              {/*
                The horn. When the light goes, an admin ends the round as it
                stands: whoever is up has won, square is halved, a quota target
                shrinks to the holes played, and the cards sign on what is
                there. Two taps, because the first one settles money.

                Not offered on the championship — called stroke play across
                three flights is a committee argument, not a button.
              */}
              {match && match.config.kind !== 'strokeNet' && (
                match.calledAt ? (
                  <div className="border border-rule px-3 py-2 flex items-center gap-2">
                    <p className="text-[13px] text-mute leading-snug flex-1">
                      <b className="bc-name text-ink">Called on darkness.</b> Every contest
                      ended as it stood; the cards sign on what is there.
                    </p>
                    <button
                      type="button"
                      onClick={() =>
                        setDraft({
                          ...draft,
                          matches: draft.matches.map((m) =>
                            m.id === match.id ? { ...m, calledAt: undefined } : m,
                          ),
                        })
                      }
                      className="bc-tap bc-label border-2 border-ink px-3 py-2 shrink-0"
                    >
                      Bring it back
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      if (armedCall !== r.id) {
                        setArmedCall(r.id)
                        return
                      }
                      setArmedCall(null)
                      setDraft({
                        ...draft,
                        matches: draft.matches.map((m) =>
                          m.id === match.id
                            ? { ...m, calledAt: new Date().toISOString() }
                            : m,
                        ),
                      })
                    }}
                    onBlur={() => armedCall === r.id && setArmedCall(null)}
                    className={`bc-tap bc-label border-2 px-3 py-2 text-left ${
                      armedCall === r.id
                        ? 'border-signal bg-signal text-paper'
                        : 'border-rule text-mute'
                    }`}
                  >
                    {armedCall === r.id
                      ? 'Tap again — ends every contest as it stands and settles the money'
                      : 'Call it on darkness — end as it stands'}
                  </button>
                )
              )}
            </li>
          )
        })}
      </ul>
      <div className="p-4">
        <button
          type="button"
          onClick={addRound}
          className="bc-tap w-full border-2 border-ink bc-label py-3"
        >
          Add a round
        </button>
      </div>

      <SectionRule>The draw</SectionRule>
      <div className="p-4 grid gap-2">
        <p className="text-[13px] text-mute leading-snug">
          Pairings are worked out, not drawn from a hat: the app spreads playing
          partners and opponents as evenly as the schedule allows, keeps the same
          foursome together across a mirrored round while re-pairing it, and balances
          group handicaps where you are ranked only against your groupmates.
        </p>
        <button
          type="button"
          onClick={drawPairings}
          className={`bc-tap w-full bc-label py-3 ${
            armedRedraw ? 'bg-signal text-paper' : 'bg-ink text-paper'
          }`}
        >
          {armedRedraw ? 'Tap again to redraw the week' : 'Generate pairings'}
        </button>
        {/*
          The answer belongs beside the question.

          There is one note for the whole screen and it renders at the top,
          which is two or three screens above this button — so the reply to a
          tap down here arrived somewhere nobody was looking, and a refusal
          read exactly like nothing happening. Showing it in both places costs
          a line and is the difference between an error and a mystery.
        */}
        {note && (
          <p className="bg-ink text-paper px-3 py-2 text-[13px] leading-snug mt-2">{note}</p>
        )}
        <button
          type="button"
          onClick={reshuffle}
          className={`bc-tap w-full bc-label py-3 border-2 ${
            armedShuffle ? 'bg-signal text-paper border-signal' : 'border-ink'
          }`}
        >
          {armedShuffle
            ? 'Tap again to reshuffle'
            : `Reshuffle (draw #${draft.settings.reshuffleCounter + 1})`}
        </button>
      </div>

      {draft.groups.length > 0 && (
        <>
          <p className="px-4 pt-2 pb-1 text-[13px] text-mute leading-snug">
            The draw decides who plays with whom; the order they go off in is yours. The
            arrows only reassign tee times — they never change who is in a group, so none
            of the fairness the optimiser worked out is lost. Changing who is in one is
            the other button, and it keeps every group id, so the cards stay signed.
          </p>
          <ul className="divide-y divide-rule">
            {inPlayOrder(draft.rounds).map((r) => {
              const inRound = draft.groups
                .filter((g) => g.roundId === r.id)
                .sort((a, b) => a.index - b.index)
              /*
               * The championship has no draw, and must not appear to have one.
               *
               * Its foursomes are the flights, and the flights come off the
               * standings walking to the first tee — so the names the optimiser
               * left in the document for this round are a placeholder for
               * scoring to hang on, nothing more. Printing them here told
               * whoever built the week that Thursday's groups were settled, four
               * days before the thing that settles them. The Preview screen has
               * always known this; the draw did not.
               *
               * The tee times stay, and stay reorderable: the times are real and
               * decided, and sending the top flight out last is a choice worth
               * having. Only the occupants are unknown.
               */
              const m = draft.matches.find((x) => x.roundId === r.id)
              const byFlight = m?.config.kind === 'strokeNet' && m.config.flighted
              return (
                <li key={r.id} className="px-4 py-3">
                  <div className="bc-label text-mute mb-1.5">{r.label}</div>
                  {byFlight && (
                    <>
                      <p className="text-[13px] text-mute leading-snug mb-1.5">
                        Who is in each flight comes off the standings once every counting card
                        is signed. Which flight goes off when is yours — move them like any
                        other group.
                      </p>
                      <button
                        type="button"
                        onClick={() => fillFlights(r.id)}
                        className={`bc-tap bc-label border-2 px-3 py-2 mb-2 ${
                          armedFill ? 'bg-signal text-paper border-signal' : 'border-ink'
                        }`}
                      >
                        {armedFill
                          ? 'Tap again to overwrite the flights'
                          : 'Set the foursomes from the standings'}
                      </button>
                    </>
                  )}
                  {seatingRound === r.id ? (
                    <>
                      {byFlight && (
                        <p className="text-[13px] text-signal leading-snug mb-1.5">
                          These foursomes are the flights, and the fill above writes them
                          from the standings — anything set by hand here is overwritten the
                          next time it is tapped.
                        </p>
                      )}
                      <SeatingEditor
                        groups={inRound}
                        bench={unseated(draft, r.id)}
                        picked={picked}
                        strokesOf={strokesIn(r)}
                        nameOf={(id) => playerName(draft, id)}
                        onTapMan={(id) => tapMan(r.id, id)}
                        onMoveTo={(groupId) => {
                          if (picked) setDraft(seatIn(draft, r.id, picked, groupId))
                          setPicked(null)
                        }}
                        onBench={() => {
                          if (picked) setDraft(seatOut(draft, r.id, picked))
                          setPicked(null)
                        }}
                      />
                    </>
                  ) : (
                    inRound.map((g, i) => (
                      <div key={g.id} className="flex items-center gap-2 py-1 text-xs">
                        <span className="bc-num text-signal w-16 shrink-0">{clockTime(g.teeTime)}</span>
                        <span className={'flex-1 min-w-0 bc-name'}>
                          {byFlight
                            ? /*
                               * The flight number stored on the group, NOT the row
                               * position — the row is the tee-time slot, and the
                               * whole point of the arrows is sending a flight out
                               * earlier or later. `?? i + 1` covers draws made
                               * before groups carried the number.
                               *
                               * Names appear only once the fill has run — before
                               * it, the group still holds the optimizer's
                               * placeholder foursome, and printing those names
                               * told whoever built the week that Wednesday was
                               * settled four days early. `flightsFilledAt` is the
                               * marker the fill leaves.
                               */
                              `Flight ${g.flight ?? i + 1}${
                                draft.settings.flightsFilledAt
                                  ? ` — ${g.playerIds.map((p) => playerName(draft, p)).join(' · ')}`
                                  : ''
                              }`
                            : g.playerIds.map((p) => playerName(draft, p)).join(' · ')}
                        </span>
                        <button
                          type="button"
                          disabled={i === 0}
                          onClick={() => moveGroup(r.id, i, -1)}
                          aria-label={`Move the ${clockTime(g.teeTime)} group earlier`}
                          className="bc-tap px-1.5 leading-none text-base disabled:opacity-20"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          disabled={i === inRound.length - 1}
                          onClick={() => moveGroup(r.id, i, 1)}
                          aria-label={`Move the ${clockTime(g.teeTime)} group later`}
                          className="bc-tap px-1.5 leading-none text-base disabled:opacity-20"
                        >
                          ↓
                        </button>
                      </div>
                    ))
                  )}

                  {/*
                    What is wrong with this round's seating, and the two buttons
                    that fix it — kept together, because a warning whose remedy
                    is three screens away is a warning nobody acts on.
                  */}
                  {(() => {
                    const problems = seatingProblems(draft, r.id)
                    /*
                     * Offered only when it would actually do something. A
                     * fivesome cannot be paired off at all, and a round whose
                     * only fault is an empty chair does not want re-pairing —
                     * in both cases a live button would answer "nothing to do",
                     * which reads as the fix having failed.
                     */
                    const canRepair =
                      Boolean(m && needsContests(m.config.kind)) &&
                      redrawContests(draft, r.id, strokesIn(r)) !== draft
                    return (
                      <>
                        {problems.length > 0 && (
                          <ul className="mt-1.5 text-[13px] text-signal leading-snug list-disc pl-4">
                            {problems.map((x) => (
                              <li key={x}>{x}</li>
                            ))}
                          </ul>
                        )}
                        <div className="flex flex-wrap gap-2 mt-2">
                          <button
                            type="button"
                            onClick={() => openSeating(r.id)}
                            // Disarms itself the moment attention moves elsewhere,
                            // exactly as the remove buttons do.
                            onBlur={() => armedSeating === r.id && setArmedSeating(null)}
                            className={`bc-tap bc-label border-2 px-3 py-2 ${
                              armedSeating === r.id
                                ? 'bg-signal text-paper border-signal'
                                : 'border-ink'
                            }`}
                          >
                            {seatingRound === r.id
                              ? 'Done'
                              : armedSeating === r.id
                                ? 'Tap again to edit the foursomes'
                                : 'Edit the foursomes'}
                          </button>
                          {canRepair && (
                            <button
                              type="button"
                              onClick={() => repair(r)}
                              className="bc-tap bc-label border-2 border-ink px-3 py-2"
                            >
                              Pair them off again
                            </button>
                          )}
                        </div>
                      </>
                    )
                  })()}
                </li>
              )
            })}
          </ul>
        </>
      )}

        </>
      )}

      {section === 'run' && (
        <>
      {/*
        What everybody is playing off, and whether it is written down.
        
        Sits directly above the start button because starting is what writes it,
        and because after that this is the only way to correct a snapshot.
      */}
      <SectionRule>Handicaps</SectionRule>
      {(() => {
        const frozen = frozenRoundIds(draft)
        const stale = new Set(staleFrozenRounds(draft))
        const rounds = [...draft.rounds].sort((a, b) => a.seq - b.seq)
        const refreeze = (ids: string[], what: string) => {
          setDraft({ ...draft, handicaps: freezeHandicaps(draft, ids) })
          setNote(`${what} re-written from the current roster.`)
        }

        if (frozen.size === 0) {
          return (
            <p className="px-4 pt-1 pb-4 text-[13px] text-mute leading-snug">
              Nothing written down yet, so every allowance is worked out live from
              the roster under Field. Starting the tournament writes them down, and from
              then on correcting somebody&rsquo;s index cannot change a round that
              has already been played.
            </p>
          )
        }

        return (
          <>
            <ul className="divide-y divide-rule border-y border-rule">
              {rounds.map((r) => {
                const isFrozen = frozen.has(r.id)
                const isStale = stale.has(r.id)
                return (
                  <li key={r.id} className="px-4 py-2.5 flex items-center gap-3">
                    <span className="min-w-0 flex-1">
                      <span className="bc-name text-sm truncate block">{r.label}</span>
                      <span className="text-[12px] text-mute truncate block">
                        {isFrozen
                          ? isStale
                            ? 'Written down, and different from the roster now'
                            : 'Written down'
                          : 'Worked out live'}
                      </span>
                    </span>
                    {/*
                      One button, and it is never destructive by surprise: it
                      rewrites THIS round only, so a round already signed for is
                      untouched unless somebody asks for it by name.
                    */}
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => refreeze([r.id], r.label)}
                      className="bc-tap bc-label border-2 border-ink px-3 py-2 shrink-0 disabled:opacity-40"
                    >
                      {isFrozen ? 'Rewrite' : 'Write'}
                    </button>
                  </li>
                )
              })}
            </ul>
            <p className="px-4 py-3 text-[13px] text-mute leading-snug">
              {stale.size > 0 ? (
                <>
                  <b className="bc-name text-ink">
                    {stale.size} round{stale.size === 1 ? '' : 's'} differ from the roster.
                  </b>{' '}
                  That is usually right — a round that has been played SHOULD keep the
                  numbers it was played off. Rewrite one only when you have changed a
                  tee or added a man for a round still to come.
                </>
              ) : (
                'Every round matches the roster. Rewrite one after changing a tee or adding a man to a round still to come.'
              )}
            </p>
          </>
        )
      })()}

      {/*
        Not a save button any more — everything above saves itself.
        
        This is the pre-flight check: the questions worth answering once, before
        twelve people start entering scores against it, and every one of them is
        something that is invisible until it costs somebody money or a round.
      */}
      <div className="p-4">
        {(() => {
          const problems = startProblems(draft)
          return (
            <>
              <button
                type="button"
                /*
                 * Latched once started. The label already changed, but the
                 * button stayed live, and a second tap re-ran the freeze from
                 * the CURRENT roster — silently discarding every hand-typed
                 * handicap override and re-deriving rounds already played. The
                 * deliberate re-freeze lives in the Handicaps section above,
                 * per round, where its blast radius is one round and visible.
                 */
                disabled={saving || problems.length > 0 || Boolean(draft.settings.startedAt)}
                onClick={() => {
                  /*
                   * Starting the week is what writes the handicaps down.
                   *
                   * Until this happens `doc.handicaps` is empty and every
                   * allowance is derived live, so a GHIN revision landing on
                   * Wednesday silently re-cuts Sunday's pops, re-scores Sunday's
                   * quota and rewrites money already signed for. Nobody sees it:
                   * the card just has different numbers than the day it was
                   * signed. This is the one moment that is unambiguously "before
                   * anybody has played", which is what makes it the right hook.
                   */
                  const handicaps = freezeHandicaps(draft)
                  setDraft({
                    ...draft,
                    handicaps,
                    settings: { ...draft.settings, startedAt: new Date().toISOString() },
                  })
                  setNote(
                    `Tournament started — the week is live. ${handicaps.length} handicaps written down.`,
                  )
                }}
                className="bc-tap w-full bg-signal text-paper bc-label py-4 disabled:opacity-40"
              >
                {draft.settings.startedAt
                  ? 'Tournament started'
                  : problems.length > 0
                    ? `${problems.length} thing${problems.length === 1 ? '' : 's'} to fix first`
                    : 'Start the tournament'}
              </button>
              {problems.length > 0 && (
                <ul className="mt-2 text-[13px] text-mute leading-snug list-disc pl-4">
                  {problems.map((m) => (
                    <li key={m}>{m}</li>
                  ))}
                </ul>
              )}
              {problems.length === 0 && !draft.settings.startedAt && (
                <p className="mt-2 text-[13px] text-mute leading-snug">
                  Everything checks out. Starting is not a lock — you can keep editing
                  afterwards; it just records that the week has begun.
                </p>
              )}
            </>
          )
        })()}
      </div>

        </>
      )}

      {section === 'trip' && (
        <>
      <SectionRule>How it looks</SectionRule>
      <p className="px-4 pt-1 pb-2 text-[13px] text-mute leading-snug">
        The four directions that were pitched before any of this was built. It applies
        to everyone, because a screen held up across a cart should match the one next
        to it. It saves itself, like everything else here.
      </p>
      <ul className="divide-y divide-rule">
        {THEMES.map((t) => {
          const on = (draft.settings.theme ?? 'broadcast') === t.id
          return (
            <li key={t.id}>
              <button
                type="button"
                onClick={() =>
                  setDraft({ ...draft, settings: { ...draft.settings, theme: t.id } })
                }
                className={`w-full text-left px-4 py-3 bc-tap flex items-center gap-3 ${
                  on ? 'bg-ink text-paper' : ''
                }`}
              >
                <span className="flex shrink-0 border border-rule">
                  {t.swatches.map((c) => (
                    <span key={c} style={{ background: c }} className="block w-5 h-7" />
                  ))}
                </span>
                <span className="min-w-0">
                  <span className="bc-name text-sm block">{t.name}</span>
                  <span className={`text-[13px] block ${on ? 'opacity-70' : 'text-mute'}`}>
                    {t.blurb}
                  </span>
                </span>
              </button>
            </li>
          )
        })}
      </ul>

        </>
      )}

      {section === 'run' && (
        <>
      <SectionRule>The money</SectionRule>
      <PayoutTotals doc={draft} />

      <DangerZone draft={draft} onNote={setNote} />
        </>
      )}
    </>
  )
}

/**
 * The foursomes of one round, laid out to be edited with one thumb.
 *
 * Tap a man to pick him up; tap a second man to trade the two; or tap a group's
 * "Move here" to drop him in on his own. Deliberately NOT drag-and-drop: this
 * is used standing on a tee, one-handed, in wind, and a drag has no 44px target
 * and no way to be undone halfway. Two taps have both — the first is visible
 * and reversible, and every control here is a real button.
 *
 * The number beside each name is his course handicap for this round, off his
 * tee, over the holes it plays. It is there because moving a man is never only
 * about who he plays with: three groups can be level and the fourth carry forty
 * strokes, and there is nothing in a list of names that says so.
 */
function SeatingEditor({
  groups,
  bench,
  picked,
  strokesOf,
  nameOf,
  onTapMan,
  onMoveTo,
  onBench,
}: {
  groups: Group[]
  /** Men on the roster with no seat in this round. */
  bench: string[]
  picked: string | null
  strokesOf: (playerId: string) => number
  nameOf: (playerId: string) => string
  onTapMan: (playerId: string) => void
  onMoveTo: (groupId: string) => void
  onBench: () => void
}) {
  const seated = picked !== null && groups.some((g) => g.playerIds.includes(picked))

  const chip = (id: string, where: string) => (
    <button
      key={id}
      type="button"
      aria-pressed={picked === id}
      // The label carries the handicap and the tee time as well as the name,
      // because a screen reader gets one string and this control's whole point
      // is the comparison between the three of them.
      aria-label={`${nameOf(id)}, ${strokesOf(id)} strokes, ${where}`}
      onClick={() => onTapMan(id)}
      className={`bc-tap border-2 border-ink px-2 py-2 flex items-center justify-between gap-2 ${
        picked === id ? 'bg-ink text-paper' : 'bg-paper'
      }`}
    >
      <span className="bc-name text-[13px] truncate">{nameOf(id)}</span>
      <span className="bc-num text-[13px] shrink-0">{strokesOf(id)}</span>
    </button>
  )

  return (
    <>
      <p className="text-[13px] text-mute leading-snug mb-2">
        Tap a man to pick him up, then tap another to trade the two, or tap a group&rsquo;s
        <b> Move here</b> to drop him in on his own. The number is his course handicap
        here. Nothing about the groups changes except who is in them — the ids, the tee
        times and the signatures on the cards all stay where they are.
      </p>

      {groups.map((g) => (
        <div key={g.id} className="border-2 border-rule mb-2">
          <div className="flex items-center gap-2 px-2 py-1.5 border-b border-rule">
            <span className="bc-num text-signal shrink-0">{clockTime(g.teeTime)}</span>
            <span className="text-[12px] text-mute flex-1 min-w-0 truncate">
              {g.flight ? `Flight ${g.flight} · ` : ''}
              {g.playerIds.length} men
            </span>
            {picked !== null && !g.playerIds.includes(picked) && (
              <button
                type="button"
                onClick={() => onMoveTo(g.id)}
                aria-label={`Move ${nameOf(picked)} to the ${clockTime(g.teeTime)} group`}
                className="bc-tap bc-label bg-ink text-paper px-2 py-1 shrink-0"
              >
                Move here
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-1.5 p-2">
            {g.playerIds.map((id) => chip(id, `in the ${clockTime(g.teeTime)} group`))}
          </div>
        </div>
      ))}

      {/*
        Somewhere to put a man who is not playing this round.

        A threesome is a real state — somebody is ill, or driving to the airport
        — and without a destination for him the only way to take him out of
        Thursday would be to take him off the roster, which clears the draw for
        every round he appears in. Shown whenever there is somebody sitting out
        OR somebody picked up, so the way out is visible at the moment it is
        wanted rather than only after it has been used.
      */}
      {(bench.length > 0 || seated) && (
        <div className="border-2 border-signal mb-2">
          <div className="flex items-center gap-2 px-2 py-1.5 border-b border-rule">
            <span className="bc-label text-signal flex-1">Not playing this round</span>
            {seated && picked !== null && (
              <button
                type="button"
                onClick={onBench}
                aria-label={`Take ${nameOf(picked)} out of the draw`}
                className="bc-tap bc-label bg-ink text-paper px-2 py-1 shrink-0"
              >
                Move here
              </button>
            )}
          </div>
          {bench.length > 0 ? (
            <div className="grid grid-cols-2 gap-1.5 p-2">
              {bench.map((id) => chip(id, 'in no group'))}
            </div>
          ) : (
            <p className="px-2 py-2 text-[13px] text-mute leading-snug">
              Everybody has a foursome.
            </p>
          )}
        </div>
      )}
    </>
  )
}

/**
 * The two actions that destroy or fork a week, kept together and kept last.
 *
 * Both ask for the word typed out. A confirm dialog is one mis-tap on a phone
 * in a pocket; typing RESET is not something you do by accident on the 1st tee.
 */
/**
 * The whole card, laid out the way the physical one is.
 *
 * You cannot honestly tick a course off without seeing what you are vouching
 * for, and stroke index is the row that matters: par and yardage errors are
 * obvious, a transposed index is invisible and wrong all week. The nines are
 * totalled separately because that is how the paper card reads and how anyone
 * checking it will scan.
 */
function CourseCard({ course }: { course: Tournament['courses'][number] }) {
  const teeIds = course.tees.map((t) => t.id)
  const nine = (from: number, to: number) => course.holes.slice(from, to)
  const sum = (hs: typeof course.holes, f: (h: (typeof hs)[number]) => number) =>
    hs.reduce((n, h) => n + f(h), 0)

  const Block = ({ label, hs }: { label: string; hs: typeof course.holes }) => (
    <>
      <tr className="bg-ink text-paper">
        <th className="bc-label text-left px-2 py-1 sticky left-0 bg-ink">{label}</th>
        {hs.map((h) => (
          <th key={h.number} className="bc-num px-1 py-1 w-8">
            {h.number}
          </th>
        ))}
        <th className="bc-label px-2 py-1">Tot</th>
      </tr>
      <tr className="bg-wash">
        <td className="bc-label text-left px-2 py-1 sticky left-0 bg-wash">Par</td>
        {hs.map((h) => (
          <td key={h.number} className="bc-num px-1 py-1 text-center">
            {h.par}
          </td>
        ))}
        <td className="bc-num px-2 py-1 text-center">{sum(hs, (h) => h.par)}</td>
      </tr>
      <tr className="border-b border-rule">
        <td className="bc-label text-left px-2 py-1 sticky left-0 bg-paper text-signal">
          Index
        </td>
        {hs.map((h) => (
          <td key={h.number} className="bc-num px-1 py-1 text-center text-signal">
            {h.strokeIndex}
          </td>
        ))}
        <td />
      </tr>
      {teeIds.map((t) => (
        <tr key={t} className="border-b border-rule">
          <td className="bc-label text-left px-2 py-1 sticky left-0 bg-paper text-mute">
            {course.tees.find((x) => x.id === t)?.name}
          </td>
          {hs.map((h) => (
            <td key={h.number} className="px-1 py-1 text-center text-mute tabular-nums">
              {h.yards[t] ?? '·'}
            </td>
          ))}
          <td className="px-2 py-1 text-center text-mute tabular-nums">
            {sum(hs, (h) => h.yards[t] ?? 0) || '·'}
          </td>
        </tr>
      ))}
    </>
  )

  return (
    <div className="mt-2 -mx-4">
      <div className="overflow-x-auto">
        <table className="text-[13px] border-collapse min-w-max">
          <tbody>
            <Block label="Out" hs={nine(0, 9)} />
            <tr className="h-2" />
            <Block label="In" hs={nine(9, 18)} />
          </tbody>
        </table>
      </div>
      <p className="px-4 pt-2 text-[12px] text-mute leading-snug">
        Check the Index row first — it decides every pop, and unlike par or yardage a
        transposed one never looks wrong. A dot is a yardage the source did not give.
      </p>

      {/*
        The tee-level numbers, which the card above does not show.
        
        `courseHandicap18` is `index x slope/113 + (rating − par)`, and that par is
        the TEE'S par. Every tee here was seeded with the course par, which is
        right for Black, Green and Gold and wrong for the family tees — Royal Blue
        is under 4,000 yards over eighteen and is not a par 72. Called one, it
        hands a 10 index a course handicap of about minus two.
        
        Shown per course rather than as one banner because the fix is per tee, and
        because somebody assigning tees on this screen needs it beside the picker
        rather than at the top of a very long page.
      */}
      {(() => {
        const problems = teeProblems(course)
        if (problems.length === 0) return null
        return (
          <div className="mx-4 mt-3 border-2 border-signal">
            <p className="bg-signal text-paper px-3 py-1.5 bc-label">
              {problems.length} tee{problems.length === 1 ? '' : 's'} to check
            </p>
            <ul className="divide-y divide-rule">
              {problems.map((x) => (
                <li key={`${x.teeId}-${x.kind}`} className="px-3 py-2">
                  <span className="bc-name text-[13px]">{x.teeName}</span>
                  <span className="text-[12px] text-mute"> — {x.detail}</span>
                </li>
              ))}
            </ul>
            <p className="px-3 py-2 text-[12px] text-mute leading-snug">
              Nobody is on these tees, and the ★ will not suggest one. Either type
              that tee&rsquo;s own par off the physical card, or take the tee off the
              list so it cannot be picked by accident.
            </p>
          </div>
        )
      })()}
    </div>
  )
}

function DangerZone({
  draft,
  onNote,
}: {
  draft: Tournament
  onNote: (s: string | null) => void
}) {
  const [confirm, setConfirm] = useState('')
  const [year, setYear] = useState(draft.year + 1)
  const [busy, setBusy] = useState<null | 'reset' | 'year'>(null)
  /*
   * Said here as well as at the top of the screen.
   *
   * These two buttons live at the very bottom of a very long page and the only
   * feedback was a note two screens above them — so a reset that finished in a
   * third of a second looked exactly like a reset that had hung. The button
   * says what it is doing, and the answer lands next to the thumb that asked.
   */
  const [said, setSaid] = useState<string | null>(null)

  const doReset = async () => {
    setBusy('reset')
    setSaid(null)
    try {
      await resetScores()
      setConfirm('')
      const done = 'Scores cleared. The setup, the draw and the signatures on the cards are reset with them.'
      setSaid(done)
      onNote(done)
    } catch (e) {
      const failed = e instanceof Error ? e.message : 'Could not reset.'
      setSaid(failed)
      onNote(failed)
    } finally {
      setBusy(null)
      setTimeout(() => onNote(null), 4000)
    }
  }

  const startYear = async () => {
    setBusy('year')
    setSaid(null)
    try {
      const id = `woodsmen-${year}`
      // Carry the roster and the courses; leave behind everything that belongs
      // to a particular week. Handicaps come across as a starting point because
      // last year's number is a far better guess than zero.
      await createTournament(id, `The Woodsmen ${year}`, year, {
        ...draft,
        id,
        name: `The Woodsmen ${year}`,
        year,
        version: 1,
        scoresEpoch: 0,
        rounds: [],
        matches: [],
        groups: [],
        pairings: [],
        handicaps: [],
        scores: [],
        tiebreaks: [],
      })
      const done = `${year} created. Use “Not you?” under This phone to sign back in and pick it.`
      setSaid(done)
      onNote(done)
    } catch (e) {
      const failed = e instanceof Error ? e.message : 'Could not create it.'
      setSaid(failed)
      onNote(failed)
    } finally {
      setBusy(null)
      setTimeout(() => onNote(null), 6000)
    }
  }

  return (
    <>
      <SectionRule>Start a new year</SectionRule>
      <p className="px-4 pt-1 pb-3 text-[13px] text-mute leading-snug">
        Copies the roster, their handicaps and the courses into a fresh year. No rounds,
        no draw, no scores. Everyone keeps the PIN they already use, and this year stays
        exactly as it is — you pick the year you want when you sign in.
      </p>
      <div className="flex gap-2 px-4 pb-4">
        <input
          type="number"
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          className="bc-field w-24 bc-num bc-tap"
        />
        <button
          type="button"
          onClick={startYear}
          disabled={busy !== null || !Number.isFinite(year)}
          className="bc-tap flex-1 border-2 border-ink bc-label py-2 disabled:opacity-30"
        >
          {busy === 'year' ? 'Creating…' : `Create ${year}`}
        </button>
      </div>
      {said && <p className="px-4 pb-4 -mt-2 text-[13px] text-mute leading-snug">{said}</p>}

      <SectionRule>Reset the scores</SectionRule>
      <p className="px-4 pt-1 pb-3 text-[13px] text-mute leading-snug">
        Deletes every score and its history for this tournament, on every phone. Players,
        handicaps, courses, rounds, formats and the draw all stay. There is no undo.
      </p>
      <div className="flex gap-2 px-4 pb-8">
        <input
          value={confirm}
          onChange={(e) => setConfirm(e.target.value.toUpperCase())}
          placeholder="Type RESET"
          className="bc-field flex-1 bc-name bc-tap"
        />
        <button
          type="button"
          onClick={doReset}
          disabled={busy !== null || confirm !== 'RESET'}
          className="bc-tap bg-signal text-paper bc-label px-5 disabled:opacity-30"
        >
          {busy === 'reset' ? 'Resetting…' : 'Reset'}
        </button>
      </div>
    </>
  )
}
